import Icon from '@/components/Icon'
import PrescriptionSection from '@/components/prescriptions/PrescriptionSection'
import RichText from '@/components/RichText'
import { WrapperWithInView } from '@/components/WrapperWithInView'
import { PrevNextButton } from '@/components/ace/subgroup/PrevNextButton'
import {
	getLinkedItems,
	useKontentHomeConfig,
} from '@/components/contexts/KontentHomeConfigProvider'
import { useWebsiteConfiguration } from '@/components/contexts/WebsiteConfigurationProvider'
import { GridCol } from '@/components/nsw/grid/GridCol'
import { GridWrapper } from '@/components/nsw/grid/GridWrapper'
import { SideNavItem } from '@/components/nsw/side-nav/SideNav'
import { FocusAreaPaper } from '@/components/papers/focusarea/FocusAreaPaper'
import FocusAreaRelatedPaper, {
	FocusAreaRelatedPaperProps,
} from '@/components/papers/focusarea/FocusAreaRelatedPaper'
import { isShowContentOutcomenotificationBasedOnStageAndYear } from '@/components/sections/content_outcomenotification'
import Contentrichtext from '@/components/sections/contentrichtext'
import { CommonCopyUrlWrapper } from '@/components/ui/copy-to-clipboard/CommonCopyUrlWrapper'
import { STAGES_WITH_LIFESKILLS } from '@/constants'
import { fetchFocusareaOrOptionslist } from '@/fetchers'
import { useIsScreenDown } from '@/hooks/useIsScreenDown'
import { useLanguagesBasedOnSyllabuses } from '@/hooks/useLanguagesBasedOnSyllabuses'
import { useRouterHash } from '@/hooks/useRouterHash'
import { useSyllabusPath } from '@/hooks/useSyllabusPath'
import {
	ContentOutcomenotification,
	Course,
	Focusareaoption,
	Optionslist,
	Outcome,
	ResourceContainer,
	Syllabus,
	Weblinkext,
	Weblinkvideo,
} from '@/kontent/content-types'
import type { Focusarea } from '@/kontent/content-types/focusarea'
import type { Teachingadvice } from '@/kontent/content-types/teachingadvice'
import {
	TaxoContentAccordion,
	TaxoLanguage,
	TaxoPathways,
} from '@/kontent/taxonomies'
import { TaxoStage } from '@/kontent/taxonomies/stage'
import { reducerContent } from '@/legacy-ported/reducers/content'
import { QS_SHOW } from '@/pages/learning-areas/[learningarea]/[syllabus]/[tab]/[stage]/[...afterStageSlugs]'
import type {
	AssetWithRawElements,
	Mapping,
	ResourceContainerOrAsset,
	TaxoStageWithLifeSkill,
	TaxoStageYearWithLifeSkill,
} from '@/types'
import { FocusareaOrOptionListOrFocusareoptionExtended } from '@/types/customKontentTypes'
import {
	byTaxoCodename,
	filterOnlyWebOutcomeNotifications,
	getFnIsItemHasStage,
	getFnIsItemHasYear,
	getSlugByCodename,
	getTaxoCodenames,
	isIntersect,
	isRichtextElementEmpty,
	isYes,
} from '@/utils'
import {
	convertToFocusareasOrOptionListOrFocusareaoptionsExtended,
	getLifeSkillsForStageLabel,
	getStagedContentBased,
	isLifeSkillFocusAreaOrOptionListOrOutcome,
} from '@/utils/focusarea'
import { TOverarchingLinkProps } from '@/utils/getLinksFromOverarchingLinks'
import { isStage6Syllabus } from '@/utils/syllabus'
import { getSyllabusTitleWithPublicationYear } from '@/utils/syllabus/getSyllabusTitleWithPublicationYear'
import {
	isAssetWithRawElement,
	isFocusarea,
	isFocusareaoption,
	isOptionList,
	isOutcome,
	isResourceContainer,
	isWebLinkVideo,
	isWebLinkext,
} from '@/utils/type_predicates'
import { css } from '@emotion/css'
import type {
	ElementModels,
	Elements,
	IContentItemsContainer,
	Responses,
} from '@kontent-ai/delivery-sdk'
import dynamic from 'next/dynamic'

import { Theme } from '@mui/material'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid, { GridProps } from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import useMediaQuery from '@mui/material/useMediaQuery'

import ContentLink from '@/components/Link'
import { useIsClient } from '@/hooks/useIsClient'
import { DownloadListField } from '@/types/downloadList'
import {
	createResourceContainerFilter,
	matchesLifeSkillsType,
	matchesNonLifeSkillsType,
} from '@/utils/resource-container'
import { getSlugByCodenameYear } from '@/utils/slug-year-utils'
import { useQuery } from '@tanstack/react-query'
import animateScrollTo from 'animated-scroll-to'
import clsx from 'clsx'
import parseHTML, {
	Element,
	attributesToProps,
	domToReact,
} from 'html-react-parser'
import Link from 'next/link'
import { useRouter } from 'next/router'
import * as NProgress from 'nprogress'
import { Alert, Button, FormGroupSelect } from 'nsw-ds-react'
import { FormOption } from 'nsw-ds-react/dist/component/forms'
import React, {
	ReactNode,
	forwardRef,
	useCallback,
	useEffect,
	useId,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useImmerReducer } from 'use-immer'
import SYLLABUS from '../../constants/syllabusConstants'
import { arrayToggleMultiple } from '../../utilities/functions'
import useFocusTabIndex from '../../utilities/hooks/useFocusTabIndex'
import CustomPopover from '../base/CustomPopover'
import OutcomeDetailCard, {
	OutcomeDetailCardTitle,
} from '../card/OutcomeDetailCard'
import TeachingSupportCard from '../card/TeachingSupportCard'
import { LeftNavProvider, useLeftNav } from '../contexts/LeftNavContext'
import { LanguagePicker } from '../custom/LanguagePicker'
import TagPicker from '../custom/TagPicker'
import {
	FocusAreaSkeleton,
	OutcomeDetailCardSkeleton,
} from './ContentDesktopSkeleton'
import {
	ContentSideNav,
	getFirstFocusAreaOptionUrlFromOptionlist,
	getFocusAreaOptionUrl,
	getUrlFromFocusArea,
} from './ContentSideNav'
import DownloadList, { DEFAULT_HIDDENS_FOR_DOWNLOAD_LIST } from './DownloadList'

const Checkbox = dynamic(() => import('@/components/nsw/Checkbox'), {
	ssr: false,
})

export interface ContentOrganizerProps {
	/**
	 * Syllabus
	 */
	syllabus: Syllabus

	/**
	 * All stages
	 */
	stages:
		| ElementModels.TaxonomyTerm<TaxoStage>[]
		| ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[]

	/**
	 * Stage id
	 */
	stageId?: TaxoStageWithLifeSkill

	/**
	 * Year Id (codenmae)
	 */
	yearId?: TaxoStageYearWithLifeSkill

	/**
	 * Support element ID
	 */
	supportElementId: string

	/**
	 * Syllabus Focus areas (the depth is 0)
	 */
	focusAreasOrOptionList?: (Focusarea | Optionslist | Focusareaoption)[]

	/**
	 * All outcomes
	 */
	outcomes?: Outcome[]

	/**
	 * All assets
	 */
	files?: (AssetWithRawElements | ResourceContainer)[]

	/**
	 * Initial state config
	 */
	initialState?: {
		accessPoints?: boolean
		teachingSupport?: boolean
		examples?: boolean
		tags?: string[]
		contentOrganiser?: string
		taScroll?: boolean
		focusAreaOption?: string
		langs?: string
		pathways?: string
		curriculumConnection?: boolean
	}

	initialStageCodename?: string
	initialYearCodename?: string

	linkedItems: IContentItemsContainer
	renderSlotContentLeft?: () => ReactNode
	lifeSkillsInfoForFocusArea?: Elements.RichTextElement

	// Filters
	hideToggleViewLifeSkills?: boolean
	hideToggleParallelContent?: boolean

	/**
	 * Focus area response
	 */
	focusAreaResponse?: Responses.IViewContentItemResponse<FocusareaOrOptionListOrFocusareoptionExtended>

	/**
	 * if true, when the focus area selected from (side) navigation,
	 * it won't redirect to the focus area page
	 */
	isFocusAreaNavigateInView?: boolean

	allOverarchingLinks?: Record<string, TOverarchingLinkProps>

	currentSyllabusCourse?: Course

	currentSyllabusCourseYearId?: TaxoStageYearWithLifeSkill

	pageType?: string

	/**
	 * Syllabus-wide web link videos (for curriculumWideAssets)
	 */
	syllabusWideWebLinkVideos?: Weblinkvideo[]

	/**
	 * Syllabus-wide web link externals (for curriculumWideAssets)
	 */
	syllabusWideWebLinkExternals?: Weblinkext[]
}

export const GridColFilter = (props: GridProps) => {
	return (
		<GridCol
			sm={6}
			width={{ xl: 'auto' }}
			maxWidth={{ xl: 'none' }}
			flexBasis={{ xl: 'auto' }}
			{...props}
		/>
	)
}

export const TEACHING_ADVICE_HIDDEN_FIELDS: DownloadListField[] = [
	...DEFAULT_HIDDENS_FOR_DOWNLOAD_LIST,
	'stage',
	'resourceType',
	'year',
]

const isQuerySameAsAccordion = (
	qStage: string,
	qYear: string,
	accordionStageId: TaxoStageWithLifeSkill,
	accordionYearId: TaxoStageYearWithLifeSkill,
) => {
	if (accordionYearId && qYear) {
		return qYear === accordionYearId
	}
	return qStage === accordionStageId
}
const getScrollFn =
	(
		el: HTMLDivElement,
		isFetchedFocusArea: boolean,
		stage: TaxoStageWithLifeSkill,
		year: TaxoStageYearWithLifeSkill,
		callback: () => void,
		speed = 0,
		targetElScroll = el,
	) =>
	() => {
		if (
			isFetchedFocusArea &&
			el?.dataset.stage === stage &&
			(!year || el?.dataset.year === year)
		) {
			animateScrollTo(targetElScroll, {
				minDuration: speed,
				maxDuration: speed,
				speed,
			})
			setTimeout(callback, 300)
		}
	}

const fnShouldShowOutcomeNotification =
	(
		currentStage: TaxoStageWithLifeSkill,
		currentYear: TaxoStageYearWithLifeSkill,
	) =>
	(notif) => {
		if (isRichtextElementEmpty(notif.elements.content)) {
			return false
		}

		return isShowContentOutcomenotificationBasedOnStageAndYear(
			notif,
			currentStage,
			currentYear,
		)
	}

const renderOutcomeNotificationList = (
	outcomeNotificationList: ContentOutcomenotification[],
	mappings: Mapping[],
	linkedItems: IContentItemsContainer,
	currentStage?: TaxoStageWithLifeSkill,
	currentYear?: TaxoStageYearWithLifeSkill,
) => {
	return (
		<div className="richtext">
			{outcomeNotificationList
				.filter(Boolean)
				.filter(
					(notif) => !isRichtextElementEmpty(notif.elements.content),
				)
				.map((notif) => {
					return (
						<RichText
							key={notif.system.id}
							richTextElement={notif.elements.content}
							mappings={mappings}
							linkedItems={linkedItems}
							currentStage={currentStage}
							currentYear={currentYear}
						/>
					)
				})}
		</div>
	)
}

const fetchFocusAreaOnContent = async (
	codename: string,
	defaultFocusAreaResponse?: Responses.IViewContentItemResponse<FocusareaOrOptionListOrFocusareoptionExtended>,
) => {
	if (defaultFocusAreaResponse) {
		return defaultFocusAreaResponse
	}
	return fetchFocusareaOrOptionslist(codename)
}

const getQueryStringBasedOnStates = ({
	showTeachingSupport,
	showAccessPoints,
	showExamples,
	selectedFilterViewLifeSkill,
	showCurriculumConnection,
}: {
	showTeachingSupport?: boolean
	showAccessPoints?: boolean
	showExamples?: boolean
	selectedFilterViewLifeSkill?: boolean
	showCurriculumConnection?: boolean
}) => {
	const show = [
		showTeachingSupport ? QS_SHOW.ADVICE : '',
		showAccessPoints ? QS_SHOW.ACCESS_CONTENT_POINTS : '',
		showExamples ? QS_SHOW.EXAMPLE : '',
		selectedFilterViewLifeSkill ? QS_SHOW.VIEW_LIFE_SKILLS : '',
		showCurriculumConnection ? QS_SHOW.CURRICULUM_CONNECTION : '',
	].filter(Boolean)

	return show
}

function getAccessPointGroupsBasedOnCurrentFocusAreaAndCurrentStageAndYear(
	currentFocusArea: Focusarea | Optionslist | Focusareaoption,
	linkedItems,
	stageId: string,
	yearId: string,
) {
	if (isFocusarea(currentFocusArea)) {
		const accessPointGroups = getLinkedItems(
			currentFocusArea.elements.accesspointgroups,
			linkedItems,
		)

		if (isLifeSkillFocusAreaOrOptionListOrOutcome(currentFocusArea)) {
			return accessPointGroups
		}
		return (
			accessPointGroups
				?.filter(getFnIsItemHasStage(stageId as TaxoStage))
				.filter(
					getFnIsItemHasYear(yearId as TaxoStageYearWithLifeSkill),
				) || []
		)
	}

	return []
}

function getOutcomesFromCurrentFocusarea(
	currentFocusArea: Focusarea | Optionslist | Focusareaoption,
	allOverarchingOutcomes: Outcome[],
	linkedItems,
	isCurrentStageLifeSkills: boolean,
	stageId: string,
	isCurrentYearLifeSkills: boolean,
	yearId: string,
	overarchingOutcomes: Outcome[],
	selectedFocusAreaOptionCodename?: string,
) {
	if (!currentFocusArea) return []

	const _isOptionList = isOptionList(currentFocusArea)
	if (_isOptionList && selectedFocusAreaOptionCodename) {
		const focusAreaOption = linkedItems[
			selectedFocusAreaOptionCodename
		] as Focusareaoption

		return getOutcomesFromCurrentFocusarea(
			focusAreaOption,
			allOverarchingOutcomes,
			linkedItems,
			isCurrentStageLifeSkills,
			stageId,
			isCurrentYearLifeSkills,
			yearId,
			overarchingOutcomes,
		)
	}
	if (!_isOptionList) {
		return [
			...allOverarchingOutcomes,
			...getLinkedItems(currentFocusArea.elements.outcomes, linkedItems)
				.filter((outcome) => isOutcome(outcome))
				.filter((outcome) => {
					// filter by stage
					if (
						isLifeSkillFocusAreaOrOptionListOrOutcome(
							currentFocusArea,
						) &&
						isCurrentStageLifeSkills
					) {
						return true
					}
					const filter = getFnIsItemHasStage(stageId as TaxoStage)
					return filter(outcome)
				})
				.filter((outcome) => {
					// filter by year
					if (
						isLifeSkillFocusAreaOrOptionListOrOutcome(
							currentFocusArea,
						) &&
						isCurrentYearLifeSkills
					) {
						return true
					}

					const filter = getFnIsItemHasYear(
						yearId as TaxoStageYearWithLifeSkill,
					)
					return filter(outcome)
				})
				.filter((outcome) => {
					// except for the ones that are already in overarching outcomes
					if (overarchingOutcomes.length) {
						return !overarchingOutcomes.some(
							(oa) =>
								oa.system.codename === outcome.system.codename,
						)
					}
					return true
				}),
		]
	}
	return []
}

function getDownloadListOfCurrentFocusArea(
	files: ResourceContainerOrAsset[],
	currentFocusArea: Focusarea | Optionslist | Focusareaoption,
	curriculumWideAssets: (
		| ResourceContainerOrAsset
		| Weblinkvideo
		| Weblinkext
	)[],
	linkedItems: IContentItemsContainer,
	selectedFocusAreaOptionCodename?: string,
) {
	if (!currentFocusArea) return []
	const _isOptionList = isOptionList(currentFocusArea)
	if (_isOptionList && selectedFocusAreaOptionCodename) {
		const focusAreaOption = linkedItems[
			selectedFocusAreaOptionCodename
		] as Focusareaoption

		return getDownloadListOfCurrentFocusArea(
			files,
			focusAreaOption,
			curriculumWideAssets,
			linkedItems,
		)
	}
	if (!_isOptionList) {
		// Cast to access resources_other since both Focusarea and Focusareaoption have it
		const focusAreaWithResources = currentFocusArea as
			| Focusarea
			| Focusareaoption

		const resourceContainers =
			getLinkedItems(
				focusAreaWithResources?.elements.resource_containers,
				linkedItems,
			) || []

		const resourcesOtherItems = getLinkedItems(
			focusAreaWithResources?.elements.resources_other,
			linkedItems,
		)

		// Filter resources_other items to only include those with required elements
		// This prevents errors in DownloadList when elements are not populated
		const validResourcesOtherItems =
			resourcesOtherItems?.filter((item) => {
				// Ensure the item has the required elements for display
				const hasRequiredElements =
					item?.elements?.resource_type && item?.elements?.syllabus
				return hasRequiredElements
			}) || []

		// Get codenames/ids of items directly linked to this focus area
		// to prevent duplicates with curriculumWideAssets
		const directlyLinkedIdentifiers = new Set([
			...resourceContainers.map((item) => item.system.codename),
			...validResourcesOtherItems.map((item) => item.system.codename),
		])

		// Filter curriculumWideAssets to exclude items already directly linked
		// Handle both IContentItem (has system.codename) and AssetWithRawElements (has id)
		const filteredCurriculumWideAssets = curriculumWideAssets.filter(
			(item) => {
				const identifier =
					'system' in item ? item.system.codename : item.id
				return !directlyLinkedIdentifiers.has(identifier)
			},
		)

		return [
			...resourceContainers,
			// Include resources_other (Weblinkext | Weblinkvideo) for Related files section
			...validResourcesOtherItems,
			...filteredCurriculumWideAssets,
		]
	}
	return []
}

function getCurrentFocusAreaTeachingAdvices(
	currentFocusArea: Focusarea | Optionslist | Focusareaoption,
	linkedItems: IContentItemsContainer,
	selectedFocusAreaOptionCodename?: string,
) {
	if (!currentFocusArea) return []
	const _isOptionList = isOptionList(currentFocusArea)
	if (_isOptionList && selectedFocusAreaOptionCodename) {
		const focusAreaOption = linkedItems[
			selectedFocusAreaOptionCodename
		] as Focusareaoption

		return getCurrentFocusAreaTeachingAdvices(focusAreaOption, linkedItems)
	}
	if (!_isOptionList && !selectedFocusAreaOptionCodename) {
		return (
			getLinkedItems(
				currentFocusArea?.elements.teachingadvice,
				linkedItems,
			) || []
		)
	}
	return []
}

function getCurrentFocusAreaRelatedFocusAreas(
	currentFocusArea: Focusarea | Optionslist | Focusareaoption,
	isFetchedFocusArea: boolean,
	linkedItems,
	stageId: string,
	yearId: string,
	selectedFocusAreaOptionCodename?: string,
	currentSyllabusCourseCodename?: string,
) {
	if (!currentFocusArea) return []
	const _isOptionList = isOptionList(currentFocusArea)
	if (_isOptionList && selectedFocusAreaOptionCodename) {
		const focusAreaOption: Focusareaoption =
			linkedItems[selectedFocusAreaOptionCodename]
		return getCurrentFocusAreaRelatedFocusAreas(
			focusAreaOption,
			isFetchedFocusArea,
			linkedItems,
			stageId,
			yearId,
		)
	}

	if (isFetchedFocusArea && currentFocusArea && !_isOptionList) {
		const relatedFocusAreas = getLinkedItems(
			currentFocusArea?.elements.related_focusareas,
			linkedItems,
		)

		return (
			relatedFocusAreas
				?.filter((focusArea) => {
					if (
						stageId === 'life_skills' ||
						isLifeSkillFocusAreaOrOptionListOrOutcome(focusArea)
					) {
						return true
					}

					return focusArea.elements.stages__stages.value.some(
						(s) => s.codename === stageId,
					)
				})
				?.filter((focusArea) => {
					if (
						!yearId ||
						yearId === 'life_skills' ||
						isLifeSkillFocusAreaOrOptionListOrOutcome(focusArea)
					) {
						return true
					}
					return focusArea.elements.stages__stage_years.value.some(
						(s) => s.codename === yearId,
					)
				})
				?.filter((focusArea) => {
					if (
						!yearId ||
						yearId === 'life_skills' ||
						isLifeSkillFocusAreaOrOptionListOrOutcome(focusArea)
					) {
						return true
					}
					if (
						currentSyllabusCourseCodename &&
						isFocusarea(focusArea)
					) {
						return focusArea.elements.course.value.some(
							(s) => s === currentSyllabusCourseCodename,
						)
					}
					return true
				}) || []
		)
	}

	return []
}

function getCurrentFocusAreaContentGroups(
	currentFocusArea: Focusarea | Optionslist | Focusareaoption,
	linkedItems,
	stageId: TaxoStageWithLifeSkill,
	yearId: TaxoStageYearWithLifeSkill,
	selectedFocusAreaOptionCodename?: string,
) {
	if (!currentFocusArea) return []
	const _isOptionList = isOptionList(currentFocusArea)
	if (_isOptionList && selectedFocusAreaOptionCodename) {
		const focusAreaOption = linkedItems[
			selectedFocusAreaOptionCodename
		] as Focusareaoption

		return getCurrentFocusAreaContentGroups(
			focusAreaOption,
			linkedItems,
			stageId,
			yearId,
		)
	}
	if (!_isOptionList) {
		const contentGroups = getLinkedItems(
			currentFocusArea.elements.contentgroups,
			linkedItems,
		)
		if (isLifeSkillFocusAreaOrOptionListOrOutcome(currentFocusArea))
			return contentGroups

		return contentGroups
			.filter(getFnIsItemHasStage(stageId))
			.filter(getFnIsItemHasYear(yearId))
	}
	return []
}

const convertFocusAreaOptionToSideNavItem = (
	fao: Focusareaoption,
	currentFocusAreaOrOptionList: Optionslist,
	currentSyllabusCodename: string,
	currentStage?: TaxoStageWithLifeSkill,
	currentYear?: TaxoStageYearWithLifeSkill,
	qs?: string[],
	otherOptions: SideNavItem | null = null,
	isNavigateInView = false,
) => {
	if (!fao || !currentFocusAreaOrOptionList) return null
	return {
		text: fao.elements.title.value,
		href: isNavigateInView
			? undefined
			: getFocusAreaOptionUrl(
					fao,
					currentFocusAreaOrOptionList,
					currentSyllabusCodename,
					currentStage,
					currentYear,
					qs,
			  ),
		...(otherOptions ?? {}),
	} as SideNavItem
}

/**
 *
 * @param cssVariableForFaOdtHeight css variable name for focus-area/focus-area-option outcome detail title height
 * @param cssVariableForStickyDDHeight css variable name for sticky dropdown height
 * @returns
 */
const getScrollMarginBasedOnODT = (
	cssVariableForFaOdtHeight = '--fa-odt-height',
	cssVariableForStickyDDHeight = '--fao-stickydd-height',
) => {
	return {
		'& [id]': {
			scrollMarginTop: `calc(var(${cssVariableForFaOdtHeight}, 0px) + var(${cssVariableForStickyDDHeight}, 0px))`,
		},
	}
}

export const getSyllabusCourseFocusAreaOutcomes = (
	currentFocusAreas,
	currentFocusAreaCodename,
	linkedItems,
	currentFocusAreaOutcomes,
): Outcome[] => {
	const syllabusCourseFocusAreas = currentFocusAreas.find(
		(focusarea) => currentFocusAreaCodename === focusarea.system.codename,
	)

	return syllabusCourseFocusAreas
		? [
				...currentFocusAreaOutcomes,
				...getLinkedItems(
					syllabusCourseFocusAreas.elements.outcomes,
					linkedItems,
				),
		  ]
		: []
}

/**
 * Previous / Next Focus Area links shown at the very end of the content panel
 * when the left nav is collapsed (full-width view). Mirrors the side-nav item
 * behaviour (deep-link href or in-view onClick) but with the two-tier design:
 * a small "Previous"/"Next" label + arrow, with the focus-area title below.
 */
const FocusAreaPrevNextNav = ({
	prev,
	next,
}: {
	prev: SideNavItem | null
	next: SideNavItem | null
}) => {
	const renderLink = (item: SideNavItem, type: 'Previous' | 'Next') => {
		const isNext = type === 'Next'
		const className = clsx(
			'group flex max-w-[45%] flex-col gap-1 no-underline',
			'[&:focus:not(:focus-visible)]:!outline-none',
			isNext ? 'items-end text-right' : 'items-start text-left',
		)
		const inner = (
			<>
				<span className="flex items-center gap-1 text-base text-nsw-grey-01">
					{!isNext && (
						<Icon icon="ic:baseline-west" width={16} height={16} />
					)}
					{type}
					{isNext && (
						<Icon icon="ic:baseline-east" width={16} height={16} />
					)}
				</span>
				<span className="font-bold text-nsw-brand-dark underline group-hover:bg-nsw-text-hover">
					{item.text}
				</span>
			</>
		)

		if (item.href) {
			return (
				<ContentLink
					href={item.href}
					scroll={item.scroll}
					shallow={item.shallow}
					onClick={item.onClick as any}
					className={className}
				>
					{inner}
				</ContentLink>
			)
		}
		return (
			<button
				type="button"
				onClick={item.onClick as any}
				className={className}
			>
				{inner}
			</button>
		)
	}

	return (
		<div className="mt-8 hidden items-start justify-between gap-6 lg:flex">
			{prev ? renderLink(prev, 'Previous') : <span />}
			{next ? renderLink(next, 'Next') : <span />}
		</div>
	)
}

const ContentInner = forwardRef<HTMLDivElement, ContentOrganizerProps>(
	(props, ref) => {
		const isClient = useIsClient()
		const isScreenDownMd = useIsScreenDown('md')
		const innerRef = useRef<HTMLDivElement>(null)
		const router = useRouter()
		const { urlRouterPath, refUrlHash } = useRouterHash()

		const { mappings, config, preview, pageResponseLinkedItems } =
			useKontentHomeConfig()

		// Get website configuration for dialog message
		const websiteConfiguration = useWebsiteConfiguration()

		const {
			syllabus,
			stageId,
			yearId,
			focusAreasOrOptionList,
			outcomes,
			initialState,
			initialStageCodename,
			initialYearCodename,
			files: _files,
			linkedItems: syllabusLinkedItems,
			lifeSkillsInfoForFocusArea,
			renderSlotContentLeft,
			hideToggleViewLifeSkills = false,
			focusAreaResponse: focusAreaOrOptionlistResponse,
			isFocusAreaNavigateInView = false,
			allOverarchingLinks = {},
			currentSyllabusCourse,
			currentSyllabusCourseYearId,
			pageType,
			syllabusWideWebLinkVideos = [],
			syllabusWideWebLinkExternals = [],
		} = props
		const refScrollHereOnFocusAreaSelected = useRef<HTMLDivElement>(null)
		const refScrollHereOnFocusAreaSelectedMobile =
			useRef<HTMLDivElement>(null)
		const refScrollHereOnTeachingSupportEnabled =
			useRef<HTMLDivElement>(null)
		const refScrollToFocusAreaContentAfterClick = useRef('')
		const refTeachingSupportTopForTitle = useRef<HTMLDivElement>(null)
		const refIsFirstLoad = useRef(true)

		const isLgLower = useMediaQuery((theme: Theme) =>
			theme.breakpoints.down('lg'),
		)

		// Early computed
		const isStage6Syl = isStage6Syllabus(syllabus)
		const qsTeachingSupport = initialState?.teachingSupport
		const qsFocusArea =
			initialState?.contentOrganiser ||
			focusAreaOrOptionlistResponse?.item.system.codename
		const qsAccessPoints = initialState?.accessPoints
		const qsExamples = initialState?.examples
		const qsCurriculumConnection = initialState?.curriculumConnection
		const qsTaScroll = initialState?.taScroll
		const qsLangs = initialState?.langs
		const qsPathways = initialState?.pathways
		const isStageYearAccordionSameWithQueryString =
			!!focusAreaOrOptionlistResponse ||
			isQuerySameAsAccordion(
				initialStageCodename,
				initialYearCodename,
				stageId,
				yearId,
			)

		const qsFocusAreaOption =
			initialState?.focusAreaOption ||
			(focusAreaOrOptionlistResponse?.item as Optionslist)?.elements
				?.focus_area_options?.value[0]

		const isWithPreloadFocusAreaAndDeepLinkNavigation =
			!!focusAreaOrOptionlistResponse && !isFocusAreaNavigateInView

		const isSyllabusHasPathways =
			syllabus.elements.pathways.value.length > 0

		const initialPathwaysBasedOnQsPathways = useMemo(() => {
			return syllabus.elements.pathways.value.filter((pathway) => {
				if (qsPathways) {
					return qsPathways.split(',').includes(pathway.codename)
				}
				return true
			})
		}, [syllabus.elements.pathways.value, qsPathways])

		const [state, dispatch] = useImmerReducer(reducerContent, {
			selectedFocusAreaCodename: isStageYearAccordionSameWithQueryString
				? qsFocusArea || ''
				: '',
			showAccessPoints: isStageYearAccordionSameWithQueryString
				? qsAccessPoints ?? false
				: false,
			showTeachingSupport: isStageYearAccordionSameWithQueryString
				? qsTeachingSupport ?? false
				: false,
			showExamples: isStageYearAccordionSameWithQueryString
				? qsExamples ?? false
				: false,
			selectedFilterViewLifeSkill:
				stageId === 'life_skills' ||
				(isStageYearAccordionSameWithQueryString &&
					isLifeSkillFocusAreaOrOptionListOrOutcome(
						isWithPreloadFocusAreaAndDeepLinkNavigation
							? focusAreaOrOptionlistResponse.item
							: focusAreasOrOptionList.find(
									(fa) => fa.system.codename === qsFocusArea,
							  ),
					)),
			selectedFocusAreaOptionCodename: qsFocusAreaOption || '',
			selectedExampleLanguages: qsLangs
				? {
						[syllabus.elements.syllabus.value[0].codename]:
							qsLangs?.split(',') as TaxoLanguage[],
				  }
				: {},
			selectedPathways: initialPathwaysBasedOnQsPathways,
			showCurriculumConnection: isStageYearAccordionSameWithQueryString
				? qsCurriculumConnection ?? false
				: false,
		})

		let {
			selectedFocusAreaCodename,
			showAccessPoints,
			showExamples,
			showTeachingSupport,
			selectedFilterViewLifeSkill,
			selectedFocusAreaOptionCodename,
			selectedExampleLanguages,
			selectedPathways,
			showCurriculumConnection,
		} = state

		const selectedExampleLanguagesArray = useMemo(() => {
			return Object.entries(selectedExampleLanguages).flatMap(
				([_, taxoLangs]) => taxoLangs,
			)
		}, [selectedExampleLanguages])

		// for tags
		const [tagsHoverPopover, setTagsHoverPopover] = useState(false)
		const [tagsPopoverAnchor, setTagsPopoverAnchor] =
			useState<HTMLElement>()
		const [pathwaysHoverPopover, setPathwaysHoverPopover] = useState(false)
		const [pathwaysPopoverAnchor, setPathwaysPopoverAnchor] =
			useState<HTMLElement>()
		const [tagIds, setTagIds] = useState(initialState?.tags)
		const [tempTagIds, setTempTagIds] = useState(initialState?.tags)
		const [tempPathways, setTempPathways] = useState(
			initialPathwaysBasedOnQsPathways,
		)
		const [tagSearchText, setTagSearchText] = useState('')
		const tagsButtonRef = useRef<HTMLButtonElement>(null)
		const pathwaysButtonRef = useRef<HTMLButtonElement>(null)

		// for left navigation collapse
		const { isLeftNavCollapsed } = useLeftNav()

		// whether the focus-area option dropdown has reached its sticky
		// position flush under the card title. While it has, the title drops
		// its own bottom border so the two do not draw a line across each
		// other; before that the border is the sticky header’s bottom edge.
		const [isFaoDropdownStuck, setIsFaoDropdownStuck] = useState(false)
		const handleFaoDropdownInViewChange = useCallback((inView: boolean) => {
			setIsFaoDropdownStuck(!inView)
		}, [])

		// computed
		const isCurrentStage4Or5Or6 = STAGES_WITH_LIFESKILLS.includes(
			stageId as any,
		)
		const isCurrentStageLifeSkills = stageId === 'life_skills'
		const isCurrentYearLifeSkills = yearId === 'life_skills'
		const currentStageBasedOnViewLifeskillsToggle =
			stageId && selectedFilterViewLifeSkill ? 'life_skills' : stageId
		const currentYearBasedOnViewLifeskillsToggle =
			yearId && selectedFilterViewLifeSkill ? 'life_skills' : yearId

		const currentFocusAreas = focusAreasOrOptionList.filter((focusArea) => {
			const isLifeSkill =
				isLifeSkillFocusAreaOrOptionListOrOutcome(focusArea)
			return selectedFilterViewLifeSkill || isCurrentStageLifeSkills
				? isLifeSkill
				: !isLifeSkill
		})

		// Left navigation only: drop focus areas whose linked course is not one
		// of this syllabus's courses. The focus areas themselves are left
		// untouched everywhere else - this only affects what the side nav lists.
		const syllabusCourseCodenames = syllabus.elements.courses?.value ?? []
		const currentFocusAreasForSideNav = currentFocusAreas.filter(
			(focusArea) => {
				// Leave option lists / life skills focus areas untouched - they
				// are not bound to the syllabus courses list
				if (
					!isFocusarea(focusArea) ||
					isLifeSkillFocusAreaOrOptionListOrOutcome(focusArea)
				) {
					return true
				}
				// If the syllabus defines no courses, don't filter (safe no-op)
				if (!syllabusCourseCodenames.length) {
					return true
				}
				return focusArea.elements.course?.value?.some((c) =>
					syllabusCourseCodenames.includes(c),
				)
			},
		)

		const currentFocusAreaSimple = useMemo(() => {
			if (focusAreaOrOptionlistResponse && !isFocusAreaNavigateInView) {
				const _focusAreaOrOptionList =
					focusAreaOrOptionlistResponse.item
				const _isItemFocusarea = isFocusarea(_focusAreaOrOptionList)
				if (
					selectedFocusAreaCodename ===
					focusAreaOrOptionlistResponse.item.system.codename
				) {
					return focusAreaOrOptionlistResponse.item
				}
				if (_isItemFocusarea) {
					if (
						_focusAreaOrOptionList.elements.related_focusareas.value.includes(
							focusAreaOrOptionlistResponse.item.system.codename,
						)
					) {
						return getLinkedItems(
							_focusAreaOrOptionList.elements.related_focusareas,
							focusAreaOrOptionlistResponse.linkedItems,
						)?.[0]
					}
				}
			}
			return focusAreasOrOptionList.find(
				(item) => item.system.codename === selectedFocusAreaCodename,
			)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			focusAreaOrOptionlistResponse?.item.system.codename,
			focusAreasOrOptionList.map((fa) => fa.system.codename).join(','), // eslint-disable-line react-hooks/exhaustive-deps
			isFocusAreaNavigateInView,
			selectedFocusAreaCodename,
		])

		const files = _files.filter((file) => {
			const currentFocusAreaSimpleSyllabus =
				currentFocusAreaSimple?.elements.syllabus.value.map(
					byTaxoCodename,
				) || []

			if (isAssetWithRawElement(file)) {
				return isIntersect(
					file.syllabus.map(byTaxoCodename),
					currentFocusAreaSimpleSyllabus,
				)
			}
			return isIntersect(
				file.elements.syllabus.value.map(byTaxoCodename),
				currentFocusAreaSimpleSyllabus,
			)
		})

		const currentFocusAreaCodename = currentFocusAreaSimple?.system.codename

		const {
			data: faResponse,
			isFetched: isFetchedFocusArea,
			isLoading,
		} = useQuery(
			[
				`focusarea_${currentFocusAreaCodename}`,
				currentFocusAreaCodename,
				focusAreaOrOptionlistResponse,
			],
			() => {
				return focusAreaOrOptionlistResponse &&
					currentFocusAreaCodename ===
						focusAreaOrOptionlistResponse.item.system.codename
					? focusAreaOrOptionlistResponse
					: fetchFocusAreaOnContent(currentFocusAreaCodename)
			},
			{
				staleTime: Infinity,
			},
		)
		const currentFocusAreaOrOptionList = useMemo(() => {
			const _temp =
				focusAreaOrOptionlistResponse?.item.system.codename &&
				!isFocusAreaNavigateInView
					? currentFocusAreaSimple
					: faResponse?.item
			if (_temp) {
				return convertToFocusareasOrOptionListOrFocusareaoptionsExtended(
					[_temp],
					syllabus,
				)[0]
			}
			return _temp
		}, [
			currentFocusAreaSimple,
			faResponse?.item,
			focusAreaOrOptionlistResponse?.item.system.codename,
			isFocusAreaNavigateInView,
			syllabus,
		])

		const isCurrentFocusAreaFocusArea = isFocusarea(
			currentFocusAreaOrOptionList,
		)

		const linkedItems = useMemo(() => {
			let _response = faResponse
			if (focusAreaOrOptionlistResponse && !isFocusAreaNavigateInView) {
				_response = focusAreaOrOptionlistResponse
			}

			return {
				...(syllabusLinkedItems || {}),
				...(_response?.linkedItems || {}),
			}
		}, [
			faResponse,
			focusAreaOrOptionlistResponse,
			isFocusAreaNavigateInView,
			syllabusLinkedItems,
		])

		const relatedLsSyllabus = getLinkedItems(
			syllabus.elements.relatedlifeskillssyllabus,
			linkedItems,
		)

		const currentFocusAreaOptions = useMemo(() => {
			if (isOptionList(currentFocusAreaOrOptionList)) {
				return getLinkedItems(
					currentFocusAreaOrOptionList.elements.focus_area_options,
					linkedItems,
				)
			}
			return []
		}, [currentFocusAreaOrOptionList, linkedItems])

		const selectedFocusAreaOption = currentFocusAreaOptions.find(
			(o) => o.system.codename === selectedFocusAreaOptionCodename,
		)

		const selectedFocusAreaOptionStagedContents = useMemo(() => {
			if (selectedFocusAreaOption) {
				return getStagedContentBased(
					selectedFocusAreaOption.elements.content_staged,
					linkedItems,
					stageId,
					yearId,
				)
			}
			return []
		}, [selectedFocusAreaOption, linkedItems, stageId, yearId])

		const currentFocusAreaOptionOrFocusAreaOrOptionlist =
			selectedFocusAreaOption || currentFocusAreaOrOptionList

		const previousAndNextFocusAreaOption = useMemo(() => {
			const currentIndex = currentFocusAreaOptions.findIndex(
				(fo) => fo.system.codename === selectedFocusAreaOptionCodename,
			)
			const prev =
				currentIndex - 1 >= 0
					? currentFocusAreaOptions[currentIndex - 1]
					: null
			const next =
				currentIndex + 1 < currentFocusAreaOptions.length
					? currentFocusAreaOptions[currentIndex + 1]
					: null
			return {
				prev,
				next,
			}
		}, [currentFocusAreaOptions, selectedFocusAreaOptionCodename])

		const focusAreasAndOrFocusAreaOptions = focusAreasOrOptionList.flatMap<
			Focusarea | Focusareaoption
		>((item) => {
			if (isOptionList(item)) {
				return getLinkedItems(
					item.elements.focus_area_options,
					linkedItems,
				)
			}
			return [item]
		})

		const currentStagedContents = isCurrentFocusAreaFocusArea
			? getStagedContentBased(
					currentFocusAreaOrOptionList?.elements.content_staged,
					linkedItems,
					stageId,
					yearId,
			  )
			: []
		const currentRelatedFocusAreasForThisAccordion = useMemo(
			() =>
				getCurrentFocusAreaRelatedFocusAreas(
					currentFocusAreaOrOptionList,
					isFetchedFocusArea,
					linkedItems,
					stageId,
					yearId,
					selectedFocusAreaOptionCodename,
					currentSyllabusCourse?.system?.codename,
				),
			[
				isFetchedFocusArea,
				currentFocusAreaOrOptionList,
				linkedItems,
				stageId,
				yearId,
				selectedFocusAreaOptionCodename,
				currentSyllabusCourse,
			],
		)

		const focusAreasOnCurrentAccordionStageOrYear = useMemo(() => {
			return (
				focusAreasAndOrFocusAreaOptions
					?.filter((focusArea) => {
						if (
							isLifeSkillFocusAreaOrOptionListOrOutcome(
								focusArea,
							) &&
							isCurrentStageLifeSkills
						) {
							return true
						}
						const filter = getFnIsItemHasStage(stageId)
						return filter(focusArea)
					})
					?.filter((focusArea) => {
						if (
							!yearId ||
							yearId === 'life_skills' ||
							isLifeSkillFocusAreaOrOptionListOrOutcome(focusArea)
						) {
							return true
						}
						return focusArea.elements.stages__stage_years.value.some(
							(s) => s.codename === yearId,
						)
					}) || []
			)
		}, [
			focusAreasAndOrFocusAreaOptions,
			isCurrentStageLifeSkills,
			stageId,
			yearId,
		])

		const overarchingOutcomes = useMemo(() => {
			return (
				focusAreasOnCurrentAccordionStageOrYear
					.flatMap((item) =>
						getLinkedItems(
							item.elements.outcomes,
							linkedItems,
						).filter(
							(outcome) =>
								isOutcome(outcome) &&
								isYes(outcome.elements.isoverarching),
						),
					)
					.reduce((acc, outcome: Outcome) => {
						if (
							!acc.some((o) => o.system.id === outcome.system.id)
						) {
							return [...acc, outcome]
						}
						return acc
					}, [] as Outcome[]) || []
			)
		}, [focusAreasOnCurrentAccordionStageOrYear, linkedItems])

		const isCurrentFocusAreaLifeSkills =
			isLifeSkillFocusAreaOrOptionListOrOutcome(
				currentFocusAreaOrOptionList,
			)

		const shouldScrollToTeachingAdviceOnLoad =
			!initialStageCodename ||
			(initialStageCodename && initialStageCodename === stageId)
		const hasLiteracyOrNumeracyTags =
			focusAreasOnCurrentAccordionStageOrYear
				.flatMap((focusArea) => {
					const _isCurrentFocusareaFA = isFocusarea(focusArea)
					const contentGroups = _isCurrentFocusareaFA
						? getLinkedItems(
								focusArea.elements.contentgroups,
								linkedItems,
						  )
						: []
					return contentGroups.flatMap((c) =>
						getLinkedItems(c.elements.content_items, linkedItems),
					)
				})
				.some((contentItem) => {
					return (
						contentItem.elements.stages__stages.value.some(
							(s) => s.codename === stageId,
						) &&
						(!!contentItem.elements
							.learningprogression_tags__literacy.value.length ||
							!!contentItem.elements
								.learningprogression_tags__numeracy.value
								.length)
					)
				})

		const outcomesNotificationsList = getLinkedItems(
			syllabus.elements.outcomesnotificationslist,
			linkedItems,
		)

		const filteredOutcomesNotificationList = outcomesNotificationsList
			.filter(filterOnlyWebOutcomeNotifications)
			.filter(
				fnShouldShowOutcomeNotification(
					currentStageBasedOnViewLifeskillsToggle,
					currentYearBasedOnViewLifeskillsToggle,
				),
			)

		const isShowOutcomesNotification =
			filteredOutcomesNotificationList?.length > 0

		// if isFocusAreaNavigateInView is false, then add query string  ?show=
		// to the focus area link, if teaching advice, content point, or example is enabled
		const qsCheckboxes = useMemo(() => {
			return getQueryStringBasedOnStates({
				showAccessPoints,
				showExamples,
				showTeachingSupport,
				showCurriculumConnection,
			})?.sort()
		}, [
			showAccessPoints,
			showExamples,
			showTeachingSupport,
			showCurriculumConnection,
		])

		const qsBySelectedPathways = useMemo<string>(() => {
			const allPathways =
				syllabus.elements.pathways.value.map(byTaxoCodename)
			const _selectedPathways = selectedPathways.map(byTaxoCodename)
			const qs = allPathways.filter((pathway) => {
				if (_selectedPathways?.length) {
					return _selectedPathways.includes(pathway)
				}
				return true
			})
			if (qs.length === allPathways.length || !qs.length) {
				return ''
			}
			return qs.join(',')
		}, [selectedPathways, syllabus.elements.pathways.value])

		const qsOnSideNavs = useMemo(() => {
			let _qs = []
			if (qsCheckboxes?.length) {
				_qs.push(`show=${qsCheckboxes.join(',')}`)

				if (qsCheckboxes.includes(QS_SHOW.ADVICE)) {
					_qs.push('ta_scroll=no')
				}
			}
			if (qsBySelectedPathways) {
				_qs.push(`paths=${qsBySelectedPathways}`)
			}
			if (selectedExampleLanguagesArray?.length) {
				_qs.push(
					'langs=' +
						selectedExampleLanguagesArray
							.filter(Boolean)
							.sort()
							.join(','),
				)
			}

			return _qs
		}, [qsCheckboxes, qsBySelectedPathways, selectedExampleLanguagesArray])

		const scrollToTeachingAdvice = useCallback(
			(
				stage: TaxoStageWithLifeSkill,
				year: TaxoStageYearWithLifeSkill,
			) => {
				const fn = getScrollFn(
					refScrollHereOnTeachingSupportEnabled.current,
					isFetchedFocusArea,
					stage,
					year,
					() => {},
				)
				// set to 450ms to wait for the accordion to open
				setTimeout(fn, 450)
			},
			[isFetchedFocusArea],
		)

		const scrollToFocusAreaContent = useCallback(
			(
				stage: TaxoStageWithLifeSkill,
				year: TaxoStageYearWithLifeSkill,
			) => {
				const $scrollFocusAreaSelected =
					refScrollHereOnFocusAreaSelected.current
				const $closestAccordion: HTMLDivElement =
					$scrollFocusAreaSelected?.closest('.nesa-accordion')

				if ($scrollFocusAreaSelected && $closestAccordion) {
					const fn = getScrollFn(
						$scrollFocusAreaSelected,
						isFetchedFocusArea,
						stage,
						year,
						() => {},
						0,
						$closestAccordion,
					)
					setTimeout(fn, 250)
				}
			},
			[isFetchedFocusArea],
		)

		const { path: syllabusPath } = useSyllabusPath(syllabus)

		const teachingAndLearningTabUrl = useMemo(() => {
			return `${syllabusPath}/teaching-and-learning`
		}, [syllabusPath])

		const focusAreaOrFocusAreaOptionPath = useMemo(() => {
			const slugFocusAreaOrOptionlist = getSlugByCodename(
				currentFocusAreaCodename || '',
			)
			const slugFocusAreaOption = getSlugByCodename(
				selectedFocusAreaOptionCodename || '',
			)
			const stageOrYear = stageId === 'stage_6' ? yearId : stageId

			const slugStageOrYear = getSlugByCodenameYear(
				currentSyllabusCourseYearId
					? currentSyllabusCourseYearId.replace('_', '-')
					: stageOrYear || '',
			)

			return [
				syllabusPath,
				'content',
				slugStageOrYear,
				slugFocusAreaOrOptionlist,
				slugFocusAreaOption,
			]
				.filter(Boolean)
				.join('/')
		}, [
			currentFocusAreaCodename,
			selectedFocusAreaOptionCodename,
			stageId,
			yearId,
			syllabusPath,
			currentSyllabusCourseYearId,
		])

		const currentTeachingAdvicePath = useMemo(() => {
			return `${focusAreaOrFocusAreaOptionPath}?show=${QS_SHOW.ADVICE}`
		}, [focusAreaOrFocusAreaOptionPath])

		const handleFilterButton = (
			event: React.MouseEvent<HTMLButtonElement>,
			filter: string,
		) => {
			if (filter === SYLLABUS.FILTER.ACCESS_POINTS) {
				dispatch({
					type: 'SET_SHOWACCESSPOINTS',
					payload: !showAccessPoints,
				})
			} else if (filter === SYLLABUS.FILTER.TEACHING_SUPPORT) {
				refIsFirstLoad.current = false
				dispatch({
					type: 'SET_SHOWTEACHINGSUPPORT',
					payload: !showTeachingSupport,
				})
				if (
					!showTeachingSupport &&
					isWithPreloadFocusAreaAndDeepLinkNavigation
				) {
					scrollToTeachingAdvice(stageId, yearId)
				}
			} else if (filter === SYLLABUS.FILTER.TAGS) {
				// if (!tempTagIds) {
				setTagsPopoverAnchor(event.currentTarget)
				setTagsHoverPopover(true)
				// } else {
				// setTempTagIds(undefined)
				// }
			} else if (filter === SYLLABUS.FILTER.EXAMPLES) {
				dispatch({
					type: 'SET_SHOWEXAMPLES',
					payload: !showExamples,
				})
			} else if (filter === SYLLABUS.FILTER.CURRICULUM_CONNECTIONS) {
				dispatch({
					type: 'SET_SHOWCURRICULUMCONNECTION',
					payload: !showCurriculumConnection,
				})
			}
		}

		const handleOnSideNavItemClick = (
			_event:
				| React.MouseEvent<HTMLDivElement, MouseEvent>
				| React.KeyboardEvent<HTMLDivElement>,
			focusArea: Focusarea,
		) => {
			const focusAreaCodename = focusArea?.system.codename
			if (isLgLower) {
				refScrollHereOnFocusAreaSelectedMobile.current.scrollIntoView()
			} else {
				refScrollHereOnFocusAreaSelected.current.scrollIntoView()
			}
			dispatch({
				type: 'SET_SELECTEDFOCUSAREACODENAME',
				payload: focusAreaCodename,
			})
			refIsFirstLoad.current = false
			refScrollToFocusAreaContentAfterClick.current = focusAreaCodename
		}

		const previousAndNextFocusAreaOptionAsSideNavItem = useMemo(() => {
			const { prev, next } = previousAndNextFocusAreaOption
			if (!isOptionList(currentFocusAreaOrOptionList)) {
				return null
			}

			if (!prev && !next) return null

			return {
				prev: prev
					? convertFocusAreaOptionToSideNavItem(
							prev,
							currentFocusAreaOrOptionList,
							syllabus.system.codename,
							stageId,
							yearId,
							qsOnSideNavs,
							{
								onClick: () => {
									dispatch({
										type: 'SET_SELECTEDFOCUSAREAOPTIONCODENAME',
										payload: prev.system.codename,
									})
									scrollToFocusAreaContent(stageId, yearId)
								},
								shallow: true,
							},
							isFocusAreaNavigateInView,
					  )
					: null,
				next: next
					? convertFocusAreaOptionToSideNavItem(
							next,
							currentFocusAreaOrOptionList,
							syllabus.system.codename,
							stageId,
							yearId,
							qsOnSideNavs,
							{
								onClick: () => {
									dispatch({
										type: 'SET_SELECTEDFOCUSAREAOPTIONCODENAME',
										payload: next.system.codename,
									})
									scrollToFocusAreaContent(stageId, yearId)
								},
								shallow: true,
							},
							isFocusAreaNavigateInView,
					  )
					: null,
			}
		}, [
			previousAndNextFocusAreaOption,
			currentFocusAreaOrOptionList,
			syllabus.system.codename,
			stageId,
			yearId,
			qsOnSideNavs,
			isFocusAreaNavigateInView,
			dispatch,
			scrollToFocusAreaContent,
		])

		// Previous / Next FOCUS AREA (not option) as side-nav items, used by the
		// full-width (collapsed) prev/next nav at the end of the content panel.
		// Mirrors how ContentSideNav builds each item so navigation behaves the
		// same (deep-link href vs in-view onClick).
		const previousAndNextFocusAreaAsSideNavItem = useMemo<{
			prev: SideNavItem | null
			next: SideNavItem | null
		} | null>(() => {
			const currentIndex = currentFocusAreas.findIndex(
				(fa) => fa.system.codename === currentFocusAreaCodename,
			)
			if (currentIndex === -1) return null

			const prevFa =
				currentIndex > 0 ? currentFocusAreas[currentIndex - 1] : null
			const nextFa =
				currentIndex < currentFocusAreas.length - 1
					? currentFocusAreas[currentIndex + 1]
					: null

			if (!prevFa && !nextFa) return null

			const currentYear = currentSyllabusCourse
				? currentSyllabusCourseYearId
				: yearId

			const toItem = (
				fa: Focusarea | Optionslist | Focusareaoption | null,
			): SideNavItem | null => {
				if (!fa) return null
				const href = isOptionList(fa)
					? getFirstFocusAreaOptionUrlFromOptionlist(
							fa,
							syllabus.system.codename,
							stageId,
							currentYear,
							qsOnSideNavs,
					  )
					: getUrlFromFocusArea(
							fa,
							syllabus.system.codename,
							stageId,
							currentYear,
							qsOnSideNavs,
							'',
							linkedItems,
					  )
				return {
					text: fa.elements.title.value,
					href: isWithPreloadFocusAreaAndDeepLinkNavigation
						? href
						: undefined,
					onClick: isWithPreloadFocusAreaAndDeepLinkNavigation
						? undefined
						: (e) =>
								handleOnSideNavItemClick(
									e as any,
									fa as Focusarea,
								),
					scroll: false,
				} as SideNavItem
			}

			return {
				prev: toItem(prevFa),
				next: toItem(nextFa),
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			currentFocusAreas,
			currentFocusAreaCodename,
			currentSyllabusCourse,
			currentSyllabusCourseYearId,
			yearId,
			stageId,
			syllabus.system.codename,
			qsOnSideNavs,
			linkedItems,
			isWithPreloadFocusAreaAndDeepLinkNavigation,
		])

		useImperativeHandle(ref, () => innerRef.current!, [])

		useEffect(() => {
			const hash = refUrlHash.current
			const $hashScrollTarget = hash ? document.querySelector(hash) : null
			if (!refIsFirstLoad.current && isFetchedFocusArea) {
				if (hash) {
					if (
						typeof window !== 'undefined' &&
						new URLSearchParams(window.location.search).has(
							'prescriptionoption',
						)
					) {
						return
					}
					if ($hashScrollTarget) {
						const rootStyle = window.getComputedStyle(
							document.documentElement,
						)
						const scrollOffset = [
							rootStyle.getPropertyValue(cssVarFaoOdtHeight) ||
								'0px',
							rootStyle.getPropertyValue(
								cssVarStickyDropdownHeight,
							) || '0px',
							'24px',
						]
							.map(parseFloat)
							.reduce((arr, curr) => arr + curr, 0)

						setTimeout(() => {
							animateScrollTo($hashScrollTarget, {
								speed: 0,
								minDuration: 0,
								maxDuration: 0,
								verticalOffset: -(
									parseFloat(
										window.getComputedStyle(
											$hashScrollTarget,
										).scrollMarginTop,
									) ||
									scrollOffset ||
									86
								),
							})
						}, 450)
						refUrlHash.current = ''
					}
					return
				}
				if (
					typeof window !== 'undefined' &&
					new URLSearchParams(window.location.search).has(
						'prescriptionoption',
					)
				) {
					return
				}
				// Use query string values directly from URL instead of component state
				// to avoid stale state issues after browser back navigation
				if (!qsTeachingSupport) {
					if (
						refScrollToFocusAreaContentAfterClick.current ===
						selectedFocusAreaCodename
					) {
						scrollToFocusAreaContent(stageId, yearId)
						refScrollToFocusAreaContentAfterClick.current = ''
					} else {
						if (isWithPreloadFocusAreaAndDeepLinkNavigation) {
							scrollToFocusAreaContent(stageId, yearId)
						}
					}
				} else {
					// when ta_scroll=no parameter, always scroll to focus area .
					if (!qsTaScroll) {
						scrollToFocusAreaContent(stageId, yearId)
					} else {
						// Only scroll to teaching advice if content actually exists (enabled)
						if (currentStageOrYearTeachingAdvices.length > 0) {
							scrollToTeachingAdvice(stageId, yearId)
						} else {
							scrollToFocusAreaContent(stageId, yearId)
						}
					}
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			isFetchedFocusArea,
			isWithPreloadFocusAreaAndDeepLinkNavigation,
			qsTaScroll,
			refUrlHash,
			scrollToFocusAreaContent,
			scrollToTeachingAdvice,
			selectedFocusAreaCodename,
			qsTeachingSupport,
			stageId,
			yearId,
			refIsFirstLoad.current,
		])

		/**
		 * Change states based on the query strings
		 */
		useEffect(() => {
			if (
				!refIsFirstLoad.current &&
				(initialStageCodename === stageId ||
					isWithPreloadFocusAreaAndDeepLinkNavigation)
			) {
				dispatch({
					type: 'SET_SELECTEDFOCUSAREAOPTIONCODENAME',
					payload: qsFocusAreaOption,
				})
				dispatch({
					type: 'SET_SELECTEDFOCUSAREACODENAME',
					payload: qsFocusArea || '',
				})
				dispatch({
					type: 'SET_SHOWACCESSPOINTS',
					payload: qsAccessPoints ?? false,
				})
				dispatch({
					type: 'SET_SHOWTEACHINGSUPPORT',
					payload: qsTeachingSupport ?? false,
				})
				dispatch({
					type: 'SET_SHOWEXAMPLES',
					payload: qsExamples ?? false,
				})
				dispatch({
					type: 'SET_SELECTEDEXAMPLELANGUAGES',
					payload: qsLangs
						? {
								[syllabus.elements.syllabus.value?.[0]
									?.codename]: qsLangs.split(
									',',
								) as TaxoLanguage[],
						  }
						: {},
				})
				dispatch({
					type: 'SET_SELECTEDPATHWAYS',
					payload: initialPathwaysBasedOnQsPathways,
				})
				dispatch({
					type: 'SET_SHOWCURRICULUMCONNECTION',
					payload: qsCurriculumConnection ?? false,
				})
				setTempPathways(initialPathwaysBasedOnQsPathways)
			}
		}, [
			dispatch,
			initialStageCodename,
			isWithPreloadFocusAreaAndDeepLinkNavigation,
			qsAccessPoints,
			qsExamples,
			qsFocusArea,
			qsTeachingSupport,
			stageId,
			currentFocusAreaOptions,
			qsFocusAreaOption,
			qsLangs,
			qsPathways,
			syllabus.elements.syllabus.value,
			initialPathwaysBasedOnQsPathways,
			qsCurriculumConnection,
		])

		/**
		 * Scroll to focus area content / teaching support on initial load
		 */
		useEffect(() => {
			const { hash = '' } = urlRouterPath || {}
			if (
				(qsFocusArea === selectedFocusAreaCodename &&
					stageId === initialStageCodename &&
					(!initialYearCodename || yearId === initialYearCodename) &&
					isFetchedFocusArea &&
					refIsFirstLoad.current) ||
				(isWithPreloadFocusAreaAndDeepLinkNavigation &&
					refIsFirstLoad.current)
			) {
				// if there's a hash, don't scroll to focus area content
				if (hash) {
					return
				}

				// Use query string values directly from URL instead of component state
				// to avoid stale state issues after browser back navigation
				let scrollFn =
					qsTeachingSupport &&
					qsTaScroll &&
					currentStageOrYearTeachingAdvices.length > 0
						? scrollToTeachingAdvice
						: scrollToFocusAreaContent
				scrollFn(stageId, yearId)
			}
		}, [
			dispatch,
			initialStageCodename,
			initialYearCodename,
			isFetchedFocusArea,
			qsFocusArea,
			scrollToFocusAreaContent,
			scrollToTeachingAdvice,
			selectedFocusAreaCodename,
			qsTeachingSupport,
			stageId,
			yearId,
			isWithPreloadFocusAreaAndDeepLinkNavigation,
			qsTaScroll,
			urlRouterPath,
		])

		/**
		 * Fix [BH-298] - issue happens when going to the url that is the same with the
		 * currently-opened url (especially for teaching advice link)
		 */
		useEffect(() => {
			const routeChangeStart = (url) => {
				const urlObj = new URL(url, window.location.origin)
				const shoulScrollForTeachingAdvice =
					url.includes('teachingSupport') &&
					shouldScrollToTeachingAdviceOnLoad

				const shouldScrollForParallelContent =
					stageId === urlObj.searchParams.get('stage')

				if (
					selectedFocusAreaCodename != qsFocusArea &&
					// same destination
					url ===
						window.location.href.replace(
							window.location.origin,
							'',
						) &&
					(shoulScrollForTeachingAdvice ||
						shouldScrollForParallelContent)
				) {
					dispatch({
						type: 'SET_SELECTEDFOCUSAREACODENAME',
						payload: qsFocusArea,
					})
					// stop the nextjs-progressbar
					NProgress.done(true)
					// stop the route change
					// eslint-disable-next-line quotes
					throw "Content.tsx aborts route change since it's going to the same path"
				}
			}
			const routeChangeComplete = () => {
				if (isFocusAreaNavigateInView) {
					refIsFirstLoad.current = true
				}
			}
			router.events.on('routeChangeStart', routeChangeStart)
			router.events.on('routeChangeComplete', routeChangeComplete)
			return () => {
				router.events.off('routeChangeStart', routeChangeStart)
				router.events.off('routeChangeComplete', routeChangeComplete)
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			selectedFocusAreaCodename,
			qsFocusArea,
			shouldScrollToTeachingAdviceOnLoad,
		])

		/**
		 * Set the state of codename of the first selected focus areas
		 */
		const firstFocusAreaCodename = currentFocusAreas?.[0]?.system.codename
		useEffect(() => {
			if (!selectedFocusAreaCodename) {
				dispatch({
					type: 'SET_SELECTEDFOCUSAREACODENAME',
					payload: firstFocusAreaCodename,
				})
			}
		}, [selectedFocusAreaCodename, firstFocusAreaCodename, dispatch])

		/**
		 * Set the state of codename of the first selected focus areas
		 */
		useEffect(() => {
			if (
				isOptionList(currentFocusAreaOrOptionList) &&
				isFocusAreaNavigateInView
			) {
				dispatch({
					type: 'SET_SELECTEDFOCUSAREAOPTIONCODENAME',
					payload:
						currentFocusAreaOrOptionList.elements.focus_area_options
							.value[0],
				})
			}
		}, [currentFocusAreaOrOptionList, isFocusAreaNavigateInView, dispatch])

		/**
		 * Change the selectedFilterViewLifeSkill based on the focusAreaSponse change
		 */
		useEffect(() => {
			if (isWithPreloadFocusAreaAndDeepLinkNavigation) {
				dispatch({
					type: 'SET_SELECTEDFILTERVIEWLIFESKILL',
					payload: isLifeSkillFocusAreaOrOptionListOrOutcome(
						focusAreaOrOptionlistResponse.item,
					),
				})
			}
		}, [
			isWithPreloadFocusAreaAndDeepLinkNavigation,
			focusAreaOrOptionlistResponse,
			dispatch,
		])

		const handleTagsChange = (ids: string[]) => {
			setTempTagIds(arrayToggleMultiple(tempTagIds ?? [], ids))
		}

		const handleTagPopoverConfirm = () => {
			setTagIds(tempTagIds)
			setTagsHoverPopover(false)
		}
		const handlePathwaysPopoverConfirm = () => {
			dispatch({
				type: 'SET_SELECTEDPATHWAYS',
				payload: !tempPathways.length
					? syllabus.elements.pathways.value
					: tempPathways,
			})
			if (!tempPathways.length) {
				setTempPathways(syllabus.elements.pathways.value)
			}
			setPathwaysHoverPopover(false)
		}

		const handleTagSearch = (text: string = 'text') => {
			setTagSearchText(text)
		}

		const handleClearAllFilters = () => {
			// Store the current value before clearing filters
			const previousFilterViewLifeSkill = selectedFilterViewLifeSkill

			// Only clear view life skills if it's enabled
			const shouldClearLifeSkills =
				!disableLifeSkillCheckbox && !isCurrentStageLifeSkills

			// Only clear examples if they are enabled
			const shouldClearExamples = shouldEnableExamplesCheckbox

			// Teaching advice is always enabled, so we can clear it
			const shouldClearTeachingAdvice = true

			dispatch({
				type: 'CLEAR_ALL_FILTERS',
				payload: {
					pathways: syllabus.elements.pathways.value,
					preserveLifeSkills:
						isFocusAreaNavigateInView || !shouldClearLifeSkills,
					preserveExamples: !shouldClearExamples,
					preserveTeachingAdvice: !shouldClearTeachingAdvice,
				},
			})
			setTagSearchText('')
			setTempTagIds([])
			setTagIds([])
			setTempPathways([...syllabus.elements.pathways.value])

			// Only run handleFocusAreaNavigation if selectedFilterViewLifeSkill changed and it's enabled
			if (
				previousFilterViewLifeSkill &&
				!isCurrentStageLifeSkills &&
				shouldClearLifeSkills
			) {
				// Filter to only Focusarea types before passing to handleFocusAreaNavigation
				handleFocusAreaNavigation({
					isFetchedFocusArea,
					focusAreasOrOptionList,
					currentSyllabusCourse,
					currentRelatedFocusAreasForThisAccordion,
					selectedFilterViewLifeSkill: previousFilterViewLifeSkill, // We know it will be false after clear
					isFocusAreaNavigateInView,
					syllabus,
					stageId,
					currentSyllabusCourseYearId,
					yearId,
					qsOnSideNavs: [],
					linkedItems,
					router,
					dispatch,
				})
			}
		}

		/**
		 * Set focus to 'Tags' button when tags popover closed
		 * Not setting focus on first render
		 */
		useFocusTabIndex(tagsHoverPopover, tagsButtonRef.current)
		useFocusTabIndex(pathwaysHoverPopover, pathwaysButtonRef.current)

		const curriculumWideAssets = useMemo(() => {
			// Helper to check if syllabuswide is set to Focus Areas (not Teaching Advice)
			const isFocusAreaSyllabusWide = (
				syllabuswide: { value: { codename: string }[] } | undefined,
			) => {
				if (!syllabuswide?.value.length) return false
				// Exclude items marked for Teaching Advice only
				return syllabuswide.value[0].codename !== 'teaching_advices'
			}

			// Filter ResourceContainer items from files
			const resourceContainerAssets =
				files
					?.filter((asset) => {
						if (isResourceContainer(asset)) {
							return isFocusAreaSyllabusWide(
								asset.elements.syllabuswide,
							)
						}
						return asset.curriculum_wide?.some(
							(t) => t.codename === 'yes',
						)
					})
					.filter((asset) => {
						if (isResourceContainer(asset)) {
							const _syllabusType = getTaxoCodenames(
								asset.elements.syllabus_type,
							)

							// if lifeskills
							if (
								stageId == 'life_skills' ||
								isCurrentFocusAreaLifeSkills
							) {
								return _syllabusType.some(matchesLifeSkillsType)
							}
							return _syllabusType.some(matchesNonLifeSkillsType)
						}
					}) || []

			// Filter syllabus-wide web link videos (Focus Areas only, not Teaching Advice)
			const syllabusWideVideos = syllabusWideWebLinkVideos.filter(
				(video) => {
					// Only include items with syllabuswide set to Focus Areas
					if (!isFocusAreaSyllabusWide(video.elements.syllabuswide))
						return false

					const _syllabusType = getTaxoCodenames(
						video.elements.syllabus_type,
					)

					// if lifeskills
					if (
						stageId == 'life_skills' ||
						isCurrentFocusAreaLifeSkills
					) {
						return _syllabusType.some(matchesLifeSkillsType)
					}
					return _syllabusType.some(matchesNonLifeSkillsType)
				},
			)

			// Filter syllabus-wide web link externals (Focus Areas only, not Teaching Advice)
			const syllabusWideExternals = syllabusWideWebLinkExternals.filter(
				(ext) => {
					// Only include items with syllabuswide set to Focus Areas
					if (!isFocusAreaSyllabusWide(ext.elements.syllabuswide))
						return false

					const _syllabusType = getTaxoCodenames(
						ext.elements.syllabus_type,
					)

					// if lifeskills
					if (
						stageId == 'life_skills' ||
						isCurrentFocusAreaLifeSkills
					) {
						return _syllabusType.some(matchesLifeSkillsType)
					}
					return _syllabusType.some(matchesNonLifeSkillsType)
				},
			)

			return [
				...resourceContainerAssets,
				...syllabusWideVideos,
				...syllabusWideExternals,
			]
		}, [
			files,
			stageId,
			isCurrentFocusAreaLifeSkills,
			syllabusWideWebLinkVideos,
			syllabusWideWebLinkExternals,
		])

		// Syllabus-wide assets for Teaching Advice section (syllabuswide = "teaching_advices")
		const curriculumWideTeachingAdviceAssets = useMemo(() => {
			// Helper to check if syllabuswide is set to Teaching Advice
			const isTeachingAdviceSyllabusWide = (
				syllabuswide: { value: { codename: string }[] } | undefined,
			) => {
				if (!syllabuswide?.value.length) return false
				return syllabuswide.value[0].codename === 'teaching_advices'
			}

			// Filter ResourceContainer items for Teaching Advice
			const resourceContainerAssets =
				files
					?.filter((asset) => {
						if (isResourceContainer(asset)) {
							return isTeachingAdviceSyllabusWide(
								asset.elements.syllabuswide,
							)
						}
						return false
					})
					.filter((asset) => {
						if (isResourceContainer(asset)) {
							const _syllabusType = getTaxoCodenames(
								asset.elements.syllabus_type,
							)

							if (
								stageId == 'life_skills' ||
								isCurrentFocusAreaLifeSkills
							) {
								return _syllabusType.some(matchesLifeSkillsType)
							}
							return _syllabusType.some(matchesNonLifeSkillsType)
						}
					}) || []

			// Filter syllabus-wide web link videos for Teaching Advice
			const syllabusWideVideos = syllabusWideWebLinkVideos.filter(
				(video) => {
					if (
						!isTeachingAdviceSyllabusWide(
							video.elements.syllabuswide,
						)
					)
						return false

					const _syllabusType = getTaxoCodenames(
						video.elements.syllabus_type,
					)

					if (
						stageId == 'life_skills' ||
						isCurrentFocusAreaLifeSkills
					) {
						return _syllabusType.some(matchesLifeSkillsType)
					}
					return _syllabusType.some(matchesNonLifeSkillsType)
				},
			)

			// Filter syllabus-wide web link externals for Teaching Advice
			const syllabusWideExternals = syllabusWideWebLinkExternals.filter(
				(ext) => {
					if (
						!isTeachingAdviceSyllabusWide(ext.elements.syllabuswide)
					)
						return false

					const _syllabusType = getTaxoCodenames(
						ext.elements.syllabus_type,
					)

					if (
						stageId == 'life_skills' ||
						isCurrentFocusAreaLifeSkills
					) {
						return _syllabusType.some(matchesLifeSkillsType)
					}
					return _syllabusType.some(matchesNonLifeSkillsType)
				},
			)

			return [
				...resourceContainerAssets,
				...syllabusWideVideos,
				...syllabusWideExternals,
			]
		}, [
			files,
			stageId,
			isCurrentFocusAreaLifeSkills,
			syllabusWideWebLinkVideos,
			syllabusWideWebLinkExternals,
		])

		// From focusarea_or_optionlist.elements.resource_containers
		const downloadListFiles = useMemo(
			() =>
				getDownloadListOfCurrentFocusArea(
					files,
					currentFocusAreaOrOptionList,
					curriculumWideAssets,
					linkedItems,
					selectedFocusAreaOptionCodename,
				),
			[
				files,
				curriculumWideAssets,
				currentFocusAreaOrOptionList,
				linkedItems,
				selectedFocusAreaOptionCodename,
			],
		)

		const currentFocusAreaTeachingAdvices = useMemo(
			() =>
				getCurrentFocusAreaTeachingAdvices(
					currentFocusAreaOrOptionList,
					linkedItems,
					selectedFocusAreaOptionCodename,
				),
			[
				currentFocusAreaOrOptionList,
				linkedItems,
				selectedFocusAreaOptionCodename,
			],
		)

		const currentStageOrYearTeachingAdvices = useMemo(() => {
			return currentFocusAreaTeachingAdvices.filter((teachingAdvice) => {
				const isStageFn = getFnIsItemHasStage(stageId)
				return (
					isStageFn(teachingAdvice) ||
					(stageId === 'life_skills' && isCurrentFocusAreaLifeSkills)
				)
			})
		}, [
			currentFocusAreaTeachingAdvices,
			isCurrentFocusAreaLifeSkills,
			stageId,
		])

		const currentFocusAreaTeachingAdvicesResources =
			currentFocusAreaTeachingAdvices.flatMap((ta) => {
				return getLinkedItems(
					ta.elements.resource_containers,
					syllabusLinkedItems,
				)
			})

		const fnMapResourceContainerBasedOnStageOrYear =
			createResourceContainerFilter(
				isStage6Syl,
				isCurrentFocusAreaLifeSkills ? 'life_skills' : stageId,
				yearId,
			)

		const teachingAdviceFiles = [
			...currentFocusAreaTeachingAdvicesResources,
			...curriculumWideTeachingAdviceAssets,
		].filter(fnMapResourceContainerBasedOnStageOrYear)

		const nonTeachingAdviceFiles = downloadListFiles
			?.filter((file) => {
				if (isAssetWithRawElement(file)) {
					return file.resource_type.some(
						(rt) => rt.codename !== 'advice',
					)
				}
				return (
					file?.elements?.resource_type?.value?.[0]?.codename !==
					'advice'
				)
			})
			.filter((file) => {
				// if is stage life skills, only return file on stage 4 and 5
				if (isCurrentStageLifeSkills || isCurrentFocusAreaLifeSkills) {
					const stages = isAssetWithRawElement(file)
						? file.stage
						: file.elements?.stages__stages?.value || []
					return stages.some(
						(s) =>
							s.codename === 'stage_4' ||
							s.codename === 'stage_5' ||
							s.codename === 'stage_6',
					)
				}
				return true
			})
			.filter((file) => {
				// Filter out items meant for Teaching Advice Resources section
				// Items with syllabuswide = "teaching_advices": EXCLUDE (they appear in teachingAdviceFiles)
				// All other items: KEEP (directly linked items and focus_areas items)
				if (isResourceContainer(file)) {
					if (file.elements.syllabuswide?.value.length) {
						const codename =
							file.elements.syllabuswide.value[0].codename
						if (codename === 'teaching_advices') return false
					}
				}
				// Also filter Weblinkvideo and Weblinkext
				if (isWebLinkVideo(file) || isWebLinkext(file)) {
					if (file.elements.syllabuswide?.value?.length) {
						const codename =
							file.elements.syllabuswide.value[0].codename
						if (codename === 'teaching_advices') return false
					}
				}
				return true
			})
			.filter((file) => {
				// Only apply resource container stage/year filter to ResourceContainer items
				// Weblinkvideo and Weblinkext items pass through without this filter
				if (isResourceContainer(file)) {
					return fnMapResourceContainerBasedOnStageOrYear(file)
				}
				return true
			})

		const downloadListHiddenFields = useMemo<DownloadListField[]>(() => {
			if (isScreenDownMd) {
				return [
					...TEACHING_ADVICE_HIDDEN_FIELDS,
					'fileSize',
					'fileType',
				]
			}
			return TEACHING_ADVICE_HIDDEN_FIELDS
		}, [isScreenDownMd])

		const currentContentOrganiserAccessPointGroups = useMemo(
			() =>
				getAccessPointGroupsBasedOnCurrentFocusAreaAndCurrentStageAndYear(
					currentFocusAreaOrOptionList,
					linkedItems,
					stageId,
					yearId,
				),
			[currentFocusAreaOrOptionList, linkedItems, stageId, yearId],
		)

		const currentFocusAreaAccessPointContent = useMemo(() => {
			if (!isCurrentFocusAreaFocusArea) return []

			const temp =
				getLinkedItems(
					currentFocusAreaOrOptionList?.elements.accesspointcontent,
					linkedItems,
				) || []
			return temp.filter((accessPointContent) => {
				if (stageId === 'life_skills' || yearId === 'life_skills')
					return true
				if (yearId) {
					return accessPointContent.elements.stage_years.value.some(
						(y) => y.codename === yearId,
					)
				}
				if (stageId) {
					return accessPointContent.elements.stages.value.some(
						(y) => y.codename === stageId,
					)
				}
				return false
			})
		}, [
			currentFocusAreaOrOptionList,
			isCurrentFocusAreaFocusArea,
			linkedItems,
			stageId,
			yearId,
		])

		const currentFocusAreaContentGroups = useMemo(() => {
			return getCurrentFocusAreaContentGroups(
				currentFocusAreaOrOptionList,
				linkedItems,
				stageId,
				yearId,
				selectedFocusAreaOptionCodename,
			)
		}, [
			currentFocusAreaOrOptionList,
			linkedItems,
			stageId,
			yearId,
			selectedFocusAreaOptionCodename,
		])

		const allOverarchingOutcomes = useMemo(() => {
			return (
				outcomes?.filter((outcome) => {
					return (
						isOutcome(outcome) &&
						!syllabus.elements.outcomes.value.some((oc) => {
							return oc === outcome.elements.code.value
						}) &&
						isYes(outcome.elements.isoverarching)
					)
				}) || []
			)
		}, [outcomes, syllabus])

		const currentFocusAreaOutcomes = useMemo(
			() =>
				getOutcomesFromCurrentFocusarea(
					currentFocusAreaOrOptionList,
					allOverarchingOutcomes,
					linkedItems,
					isCurrentStageLifeSkills,
					stageId,
					isCurrentYearLifeSkills,
					yearId,
					overarchingOutcomes,
					selectedFocusAreaOptionCodename,
				).filter((outcome) => {
					if (!isStage6Syl && isYes(outcome.elements.isoverarching)) {
						return true
					}
					return selectedFilterViewLifeSkill
						? outcome.elements.syllabus_type__items.value[0]
								.codename === 'life_skills'
						: outcome.elements.syllabus_type__items.value[0]
								.codename !== 'life_skills'
				}),
			[
				currentFocusAreaOrOptionList,
				allOverarchingOutcomes,
				linkedItems,
				isCurrentStageLifeSkills,
				stageId,
				isCurrentYearLifeSkills,
				yearId,
				overarchingOutcomes,
				selectedFocusAreaOptionCodename,
				selectedFilterViewLifeSkill,
				isStage6Syl,
			],
		)

		const disableLifeSkillCheckbox = useMemo(() => {
			if (isCurrentStage4Or5Or6) {
				// get all the syllabus taxos of related life skills syllabus
				const syllabusTaxosOfRelatedLs = [
					...(syllabus?.elements.syllabus.value.map(byTaxoCodename) ||
						[]),
					...relatedLsSyllabus.flatMap((r) =>
						r.elements.syllabus.value.map(byTaxoCodename),
					),
				]

				const mainFocusArea =
					selectedFocusAreaOption ||
					(currentFocusAreaOrOptionList as Focusarea)

				if (!mainFocusArea) return true

				const relatedFocusArea = getLinkedItems(
					mainFocusArea.elements.related_focusareas,
					linkedItems,
				)

				/**
				 * disable if there are no related focus areas
				 * or if there's any, the related focus areas don't have the same syllabus with:
				 * a. the currently opened syllabus
				 * b. the related life skill syllabus of the currently opened syllabus
				 */

				return (
					!currentRelatedFocusAreasForThisAccordion.length ||
					!isIntersect(
						relatedFocusArea?.flatMap((r) =>
							r.elements.syllabus.value.map(byTaxoCodename),
						) || [],
						syllabusTaxosOfRelatedLs,
					)
				)
			}
			return false
		}, [
			isCurrentStage4Or5Or6,
			syllabus.elements.syllabus.value,
			relatedLsSyllabus,
			selectedFocusAreaOption,
			currentFocusAreaOrOptionList,
			linkedItems,
			currentRelatedFocusAreasForThisAccordion.length,
		])

		const syllabusesForExampleLanguagePicker = useMemo(() => {
			return [syllabus]
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [syllabus.system.codename])

		// Generate dynamic language picker title based on current syllabus
		const languagePickerTitle = useMemo(() => {
			return getSyllabusTitleWithPublicationYear(syllabus, false)
		}, [syllabus])

		// Generate slotBeforeSearch content from website configuration
		const slotBeforeSearchContent = useMemo(() => {
			// If no website configuration field exists, return default content
			const richTextElement =
				websiteConfiguration?.item?.elements
					?.message_select_language_dialog
			if (!richTextElement?.value) {
				return (
					<p>
						Language selection only applies to language specific
						examples.
					</p>
				)
			}

			// Use RichText component to properly process links and content
			return (
				<RichText
					richTextElement={richTextElement}
					linkedItems={pageResponseLinkedItems}
					mappings={mappings}
					currentSyllabus={syllabus}
				/>
			)
		}, [websiteConfiguration, syllabus, mappings, pageResponseLinkedItems])

		const {
			languageOptions,
			isShowLanguageOptions,
			toggleLanguageOptions,
		} = useLanguagesBasedOnSyllabuses({
			selectedSyllabusCodenames: syllabusesForExampleLanguagePicker.map(
				(syl) => syl.system.codename,
			),
			syllabuses: syllabusesForExampleLanguagePicker,
		})

		const shouldShowExamplesCheckboxShow = useMemo(() => {
			// if it's stage 6 syllabus
			if (isStage6Syl) {
				// if current focus area is life skills
				if (isCurrentFocusAreaLifeSkills && relatedLsSyllabus?.[0]) {
					return isYes(relatedLsSyllabus[0].elements.has_examples)
				}
			}
			// otherwise
			return isYes(syllabus.elements.has_examples)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			isStage6Syl,
			syllabus.system.codename,
			isCurrentFocusAreaLifeSkills,
			stageId,
			yearId,
		])

		const shouldEnableExamplesCheckbox = useMemo(() => {
			const fnMatchTheStageOrYear = (
				stageOrYear: ElementModels.TaxonomyTerm<TaxoContentAccordion>,
			) => {
				return (
					stageOrYear.codename === yearId ||
					stageOrYear.codename === stageId
				)
			}

			// if it's stage 6 syllabus
			if (isStage6Syl) {
				// if current focus area is life skills
				if (isCurrentFocusAreaLifeSkills && relatedLsSyllabus?.[0]) {
					return relatedLsSyllabus[0].elements.has_examples_in.value.some(
						fnMatchTheStageOrYear,
					)
				}
			}
			// otherwise
			return syllabus.elements.has_examples_in.value.some(
				fnMatchTheStageOrYear,
			)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			isStage6Syl,
			syllabus.system.codename,
			isCurrentFocusAreaLifeSkills,
			stageId,
			yearId,
		])

		const selectedPathwaysBasedOnSyllabus = useMemo(() => {
			/**
			 * if syllabus has pathways, return the selected pathways,
			 * otherwise, return an array with one object that mimicks TaxoPathways,
			 * but empty codename and name, so that it won't group the content items
			 */

			return isSyllabusHasPathways
				? syllabus.elements.pathways.value.filter((p) =>
						selectedPathways.some(
							(sp) => sp.codename === p.codename,
						),
				  )
				: [
						{
							codename: '' as TaxoPathways,
							name: '',
						} as ElementModels.TaxonomyTerm<TaxoPathways>,
				  ]
		}, [
			isSyllabusHasPathways,
			syllabus.elements.pathways.value,
			selectedPathways,
		])

		const isShowClearAllFilters = useMemo(() => {
			const _selectedExampleLanguages = Object.values(
				selectedExampleLanguages,
			).flat()

			return (
				(showAccessPoints &&
					currentContentOrganiserAccessPointGroups?.length > 0) ||
				showTeachingSupport ||
				(showExamples && shouldEnableExamplesCheckbox) ||
				showCurriculumConnection ||
				(!isCurrentStageLifeSkills &&
					!disableLifeSkillCheckbox &&
					selectedFilterViewLifeSkill) ||
				_selectedExampleLanguages.length > 0 ||
				(isSyllabusHasPathways &&
					selectedPathways.length > 0 &&
					selectedPathways.length !=
						syllabus.elements.pathways.value.length) ||
				tagIds?.length > 0
			)
		}, [
			showAccessPoints,
			currentContentOrganiserAccessPointGroups,
			showTeachingSupport,
			showExamples,
			shouldEnableExamplesCheckbox,
			showCurriculumConnection,
			selectedFilterViewLifeSkill,
			selectedExampleLanguages,
			selectedPathways,
			isSyllabusHasPathways,
			tagIds,
			syllabus.elements.pathways.value,
			isCurrentFocusAreaLifeSkills,
			isCurrentStageLifeSkills,
			disableLifeSkillCheckbox,
		])

		const uuid = useId()
		const lifeSkillCheckboxId = `ls-${uuid}`,
			teachingSupportCheckboxId = `ts-${uuid}`,
			examplesCheckboxId = `ex-${uuid}`,
			accessPointsCheckboxId = `ap-${uuid}`

		const cssVariableId = useId()
		const uuidAlphaNumericsOnly = cssVariableId.replace(/[^a-zA-Z0-9]/g, '')

		// set css variable for focus area options dropdown - replace all non-alphanumeric characters with blank
		const cssVarFaoOdtHeight = `--fao-odt-height-${uuidAlphaNumericsOnly}`
		const cssVarStickyDropdownHeight = `--fao-stickydd-height-${uuidAlphaNumericsOnly}`

		/**
		 * Handler
		 */
		const onRelatedFocusAreaTitleClick: FocusAreaRelatedPaperProps['onTitleClick'] =
			(_e, _focusArea) => {
				let focusAreaCodename = _focusArea.system.codename

				if (isFocusareaoption(_focusArea)) {
					// get option list
					const optionlist = focusAreasOrOptionList.find((f) => {
						if (isOptionList(f)) {
							return f.elements.focus_area_options.value.some(
								(o) => o === _focusArea.system.codename,
							)
						}
						return false
					})
					focusAreaCodename =
						optionlist?.system.codename || focusAreaCodename
				}

				dispatch({
					type: 'TOGGLE_SELECTEDLIFESKILLFOCUSAREA',
					payload: { focusAreaCodename },
				})
				refScrollToFocusAreaContentAfterClick.current =
					focusAreaCodename
			}

		/**
		 * Render "No teaching advice"
		 */
		const renderNoTeachingAdvice = () => {
			return (
				<TeachingSupportCard
					content={null}
					mappings={mappings}
					linkedItems={linkedItems}
				>
					{websiteConfiguration?.item?.elements
						?.nocontent_teachingadvice?.value ? (
						<p>
							{parseHTML(
								websiteConfiguration.item.elements.nocontent_teachingadvice.value.replace(
									'teaching and learning support tab',
									`<a href="${teachingAndLearningTabUrl}">teaching and learning support tab</a>`,
								),
								{
									replace: (domNode: Element) => {
										if (
											domNode.type === 'tag' &&
											domNode.name === 'a'
										) {
											const attributes =
												attributesToProps(
													domNode.attribs,
												)

											return (
												<Link href={attributes.href}>
													<a>
														{domToReact(
															domNode.children,
														)}
													</a>
												</Link>
											)
										}
									},
								},
							)}
						</p>
					) : (
						<p>
							There is no teaching advice for this content. Please
							refer to the{' '}
							<Link href={teachingAndLearningTabUrl}>
								<a>
									teaching and learning support tab of the
									syllabus for support materials
								</a>
							</Link>
						</p>
					)}
				</TeachingSupportCard>
			)
		}

		const handleFocusareaOptionsDropdownChange = (
			e: React.ChangeEvent<HTMLSelectElement>,
		): void => {
			dispatch({
				type: 'SET_SELECTEDFOCUSAREAOPTIONCODENAME',
				payload: e.target.value,
			})

			if (
				isWithPreloadFocusAreaAndDeepLinkNavigation &&
				isOptionList(currentFocusAreaOrOptionList)
			) {
				const nextFocusAreaUrl = getFocusAreaOptionUrl(
					currentFocusAreaOptions.find(
						(fao) => fao.system.codename === e.target.value,
					),
					currentFocusAreaOrOptionList,
					syllabus.system.codename,
					stageId,
					yearId,
					qsOnSideNavs,
				)

				router.push(nextFocusAreaUrl, undefined, {
					shallow: true,
					scroll: false,
				})
			}

			// Scroll to focus area content
			scrollToFocusAreaContent(stageId, yearId)
		}

		// set the height of the sticky focus area option dropdown
		useEffect(() => {
			if (!innerRef.current || isCurrentFocusAreaFocusArea) return

			const stickyFaoDropdown = innerRef.current.querySelector(
				'.Content__faoDropdown',
			)

			if (!stickyFaoDropdown) return

			const observer = new ResizeObserver((entries) => {
				for (let entry of entries) {
					const height = entry.borderBoxSize?.[0]?.blockSize
					document.documentElement.style.setProperty(
						cssVarStickyDropdownHeight,
						`${height}px`,
					)
				}
			})
			observer.observe(stickyFaoDropdown)
			return () => {
				observer.disconnect()
			}
		}, [isCurrentFocusAreaFocusArea, cssVarStickyDropdownHeight])

		/**
		 * Shallow route-push based on the states
		 */
		useEffect(() => {
			if (
				isWithPreloadFocusAreaAndDeepLinkNavigation &&
				!refIsFirstLoad.current
			) {
				const currentUrl = new URL(window.location.href)
				const url = new URL(window.location.href)

				const qsCheckboxesSorted = qsCheckboxes.sort().join(',')
				const qsLangsSorted = selectedExampleLanguagesArray
					.sort()
					.join(',')
				const qsPathwaysSorted = selectedPathways
					.map(byTaxoCodename)
					.sort()
					.join(',')

				// if there's no advice in the query string, remove the ta_scroll
				if (!qsCheckboxesSorted?.includes('advice')) {
					url.searchParams.delete('ta_scroll')
				}

				if (qsCheckboxesSorted) {
					url.searchParams.set('show', qsCheckboxesSorted)
					if (qsCheckboxesSorted.includes('example')) {
						if (qsLangsSorted) {
							url.searchParams.set('langs', qsLangsSorted)
						} else {
							url.searchParams.delete('langs')
						}
					}
				} else {
					url.searchParams.delete('show')
					url.searchParams.delete('langs')
				}

				if (
					qsPathwaysSorted &&
					qsPathwaysSorted.split(',').length !==
						syllabus.elements.pathways.value.length
				) {
					url.searchParams.set('paths', qsPathwaysSorted)
				} else {
					url.searchParams.delete('paths')
				}

				if (!url.searchParams.has('prescriptionoption')) {
					url.hash = ''
				}
				if (currentUrl.href !== url.href) {
					router.replace(url, undefined, {
						shallow: true,
						scroll: false,
					})
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			qsCheckboxes,
			refIsFirstLoad.current,
			isWithPreloadFocusAreaAndDeepLinkNavigation,
			selectedExampleLanguagesArray,
			selectedPathways,
			syllabus.elements.pathways.value.length,
		])

		useEffect(() => {
			refIsFirstLoad.current = false
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [refIsFirstLoad.current])

		/**
		 * Handles focus area navigation and selection
		 */
		const handleFocusAreaNavigation = ({
			isFetchedFocusArea,
			focusAreasOrOptionList,
			currentSyllabusCourse,
			currentRelatedFocusAreasForThisAccordion,
			selectedFilterViewLifeSkill,
			isFocusAreaNavigateInView,
			syllabus,
			stageId,
			currentSyllabusCourseYearId,
			yearId,
			qsOnSideNavs,
			linkedItems,
			router,
			dispatch,
		}: {
			isFetchedFocusArea: boolean
			focusAreasOrOptionList: (
				| Focusarea
				| Optionslist
				| Focusareaoption
			)[]
			currentSyllabusCourse?: Course
			currentRelatedFocusAreasForThisAccordion?: (
				| Focusarea
				| Focusareaoption
			)[]
			selectedFilterViewLifeSkill: boolean
			isFocusAreaNavigateInView: boolean
			syllabus: Syllabus
			stageId: TaxoStageWithLifeSkill
			currentSyllabusCourseYearId?: TaxoStageYearWithLifeSkill
			yearId: TaxoStageYearWithLifeSkill
			qsOnSideNavs: string[]
			linkedItems: IContentItemsContainer
			router: any
			dispatch?: any
		}) => {
			if (!isFetchedFocusArea) {
				return
			}

			const firstFocusAreaMS = currentSyllabusCourse
				? focusAreasOrOptionList?.find(
						(focusArea) =>
							!isLifeSkillFocusAreaOrOptionListOrOutcome(
								focusArea,
							) &&
							isFocusarea(focusArea) &&
							focusArea.elements.course?.value.some(
								(a) =>
									a === currentSyllabusCourse.system.codename,
							),
				  )
				: focusAreasOrOptionList?.find(
						(focusArea) =>
							!isLifeSkillFocusAreaOrOptionListOrOutcome(
								focusArea,
							),
				  )

			const firstFocusAreaLS = currentSyllabusCourse
				? focusAreasOrOptionList?.find((focusArea) => {
						return (
							isFocusarea(focusArea) &&
							focusArea.elements.course?.value.some(
								(a) =>
									a === currentSyllabusCourse.system.codename,
							)
						)
				  })
				: focusAreasOrOptionList?.find((focusArea) =>
						isLifeSkillFocusAreaOrOptionListOrOutcome(focusArea),
				  )

			const extensionCourse =
				currentRelatedFocusAreasForThisAccordion?.filter(
					(a) =>
						a.system.codename ===
						(selectedFilterViewLifeSkill
							? firstFocusAreaMS.system.codename
							: firstFocusAreaLS.system.codename),
				)?.[0]

			let nextFocusArea =
				convertToFocusareasOrOptionListOrFocusareaoptionsExtended(
					[
						currentRelatedFocusAreasForThisAccordion?.[0] ||
							(currentSyllabusCourse
								? extensionCourse
								: selectedFilterViewLifeSkill
								? firstFocusAreaMS
								: firstFocusAreaLS),
					] as Focusarea[],
					syllabus,
				)[0]

			if (!isFocusAreaNavigateInView) {
				const nextFocusAreaUrl = getUrlFromFocusArea(
					nextFocusArea,
					syllabus.system.codename,
					stageId,
					currentSyllabusCourse
						? (currentSyllabusCourseYearId.replace(
								'_',
								'-',
						  ) as TaxoStageYearWithLifeSkill)
						: yearId,
					qsOnSideNavs,
					'',
					linkedItems,
				)

				router.push(nextFocusAreaUrl, undefined, {
					scroll: false,
				})
				return
			}

			nextFocusArea = isFocusareaoption(nextFocusArea)
				? focusAreasOrOptionList.find((f) => {
						if (isOptionList(f)) {
							return f.elements.focus_area_options.value.some(
								(o) => o === nextFocusArea.system.codename,
							)
						}
						return false
				  })
				: nextFocusArea

			dispatch({
				type: 'TOGGLE_SELECTEDLIFESKILLFOCUSAREA',
				payload: {
					focusAreaCodename: nextFocusArea?.system.codename,
				},
			})
		}

		return (
			<div ref={innerRef}>
				<div className="space-y-4 md:space-y-8 lg:space-y-0">
					{/* Filters */}
					<div>
						<GridWrapper
							spacing={{ xs: 4, lg: 6 }}
							alignItems={{ xl: 'center' }}
						>
							{!hideToggleViewLifeSkills && (
								<GridColFilter>
									<FormControlLabel
										htmlFor={lifeSkillCheckboxId}
										className="mx-0"
										checked={selectedFilterViewLifeSkill}
										control={
											<Checkbox
												id={lifeSkillCheckboxId}
												disabled={
													disableLifeSkillCheckbox
												}
											/>
										}
										label="View Life Skills"
										onChange={() => {
											handleFocusAreaNavigation({
												isFetchedFocusArea,
												focusAreasOrOptionList,
												currentSyllabusCourse,
												currentRelatedFocusAreasForThisAccordion,
												selectedFilterViewLifeSkill,
												isFocusAreaNavigateInView,
												syllabus,
												stageId,
												currentSyllabusCourseYearId,
												yearId,
												qsOnSideNavs,
												linkedItems,
												router,
												dispatch,
											})
										}}
										disabled={disableLifeSkillCheckbox}
									/>
								</GridColFilter>
							)}
							<GridColFilter>
								<FormControlLabel
									htmlFor={teachingSupportCheckboxId}
									className="mx-0"
									checked={showTeachingSupport}
									control={
										<Checkbox
											id={teachingSupportCheckboxId}
										/>
									}
									label="Teaching advice"
									onChange={(e) =>
										handleFilterButton(
											e as any,
											SYLLABUS.FILTER.TEACHING_SUPPORT,
										)
									}
								/>
							</GridColFilter>
							{shouldShowExamplesCheckboxShow && (
								<GridColFilter className="flex items-center gap-2">
									<FormControlLabel
										htmlFor={examplesCheckboxId}
										className="mx-0"
										checked={
											shouldEnableExamplesCheckbox &&
											showExamples
										}
										control={
											<Checkbox id={examplesCheckboxId} />
										}
										label="Examples"
										onChange={(e) => {
											if (
												languageOptions.length &&
												!(
													shouldEnableExamplesCheckbox &&
													showExamples
												)
											) {
												toggleLanguageOptions(true)
											}
											// Reset example languages when changing this toggle
											dispatch({
												type: 'SET_SELECTEDEXAMPLELANGUAGES',
												payload: {},
											})
											return handleFilterButton(
												e as any,
												SYLLABUS.FILTER.EXAMPLES,
											)
										}}
										disabled={!shouldEnableExamplesCheckbox}
									/>
									{syllabus.elements.languages.value.length >
										0 &&
										shouldEnableExamplesCheckbox &&
										showExamples && (
											<button
												className="w-[1.875rem] h-[1.875rem]"
												type="button"
												onClick={() => {
													toggleLanguageOptions()
												}}
												aria-label="Show language filters"
											>
												<Icon icon="mdi:chevron-down" />
											</button>
										)}
								</GridColFilter>
							)}
							{currentContentOrganiserAccessPointGroups?.length >
								0 && (
								<GridColFilter>
									<FormControlLabel
										htmlFor={accessPointsCheckboxId}
										className="mx-0"
										checked={showAccessPoints}
										control={
											<Checkbox
												id={accessPointsCheckboxId}
											/>
										}
										label="Access content points"
										onChange={(e) =>
											handleFilterButton(
												e as any,
												SYLLABUS.FILTER.ACCESS_POINTS,
											)
										}
									/>
								</GridColFilter>
							)}
							<GridColFilter
								xs={12}
								sm={12}
								display={{ xs: 'flex' }}
								flex={{ xs: '1 1 0%' }}
								justifyContent={{ xl: 'flex-end' }}
								gap={{ xs: 2 }}
								className="gap-6"
							>
								{/* Clear all filters */}
								{isShowClearAllFilters && (
									<button
										className="flex-1 xl:flex-initial text-nsw-brand-dark font-bold underline"
										onClick={handleClearAllFilters}
									>
										Reset selection
									</button>
								)}

								{hasLiteracyOrNumeracyTags && (
									<Button
										ref={tagsButtonRef}
										style="dark-outline"
										className={clsx(
											'flex-1 xl:flex-initial',
											tagIds
												? 'button--selected'
												: 'nsw-button--secondary',
										)}
										onClick={(e) =>
											handleFilterButton(
												e,
												SYLLABUS.FILTER.TAGS,
											)
										}
										tabIndex={0}
									>
										<span className="mr-2">
											{SYLLABUS.FILTER.TAGS}
										</span>
										<Icon icon="mdi:chevron-down"></Icon>
									</Button>
								)}
								{isSyllabusHasPathways && (
									<Button
										ref={pathwaysButtonRef}
										style="dark-outline"
										className={clsx(
											'flex-1 xl:flex-initial',
											'nsw-button--secondary',
										)}
										onClick={(e) => {
											setPathwaysPopoverAnchor(
												e.currentTarget,
											)
											setPathwaysHoverPopover((v) => {
												return !v
											})
										}}
									>
										<span className="mr-2">
											Pathways ({selectedPathways.length})
										</span>
										<Icon icon="mdi:chevron-down"></Icon>
									</Button>
								)}
							</GridColFilter>
						</GridWrapper>
					</div>
					{/* Mobile: current focus area */}
					<div
						ref={refScrollHereOnFocusAreaSelectedMobile}
						className="mt-8 lg:hidden"
					>
						<GridWrapper>
							<GridCol md={6}>
								{!isFetchedFocusArea ||
								isLoading ||
								!currentFocusAreaOrOptionList ? (
									<FocusAreaSkeleton />
								) : currentFocusAreaOrOptionList ? (
									<FocusAreaPaper
										currentStage={stageId}
										focusAreaOrOptionlistOrFocusareaoption={
											currentFocusAreaOrOptionList
										}
										outcomes={
											currentSyllabusCourse
												? getSyllabusCourseFocusAreaOutcomes(
														currentFocusAreas,
														currentFocusAreaCodename,
														linkedItems,
														currentFocusAreaOutcomes,
												  )
												: currentFocusAreaOutcomes
										}
										linkedItems={linkedItems}
										mappings={mappings}
										isLifeSkill={isLifeSkillFocusAreaOrOptionListOrOutcome(
											currentFocusAreaOrOptionList,
										)}
										slotBeforeOutcomes={
											isShowOutcomesNotification && (
												<Alert
													as="info"
													className="mb-4 text-nsw-grey-01"
												>
													{renderOutcomeNotificationList(
														filteredOutcomesNotificationList,
														mappings,
														linkedItems,
														currentStageBasedOnViewLifeskillsToggle,
														currentYearBasedOnViewLifeskillsToggle,
													)}
												</Alert>
											)
										}
										selected
									/>
								) : null}
							</GridCol>
							<GridCol
								md={6}
								display="flex"
								flexDirection="column"
							>
								{!isFetchedFocusArea ||
								isLoading ||
								!currentFocusAreaOrOptionList ? (
									<FocusAreaSkeleton />
								) : isCurrentStage4Or5Or6 &&
								  currentFocusAreaOrOptionList ? (
									<FocusAreaRelatedPaper
										className="flex-1"
										syllabus={syllabus}
										mainFocusArea={
											selectedFocusAreaOption ||
											(currentFocusAreaOrOptionList as Focusarea)
										}
										currentStage={stageId}
										currentYear={
											currentSyllabusCourse
												? currentSyllabusCourseYearId
												: yearId
										}
										onTitleClick={
											!isWithPreloadFocusAreaAndDeepLinkNavigation
												? onRelatedFocusAreaTitleClick
												: undefined
										}
										linkedItems={linkedItems}
										qsOnUrl={qsOnSideNavs}
										currentCourseCodename={
											currentSyllabusCourse
												? currentSyllabusCourse.system
														.codename
												: null
										}
									/>
								) : null}
							</GridCol>
						</GridWrapper>
					</div>

					{/* Content */}
					<div>
						<Grid
							container
							spacing={8}
							className="flex-col-reverse lg:flex-row lg:mt-0"
						>
							{/* Left: Side Navigation */}
							<GridCol
								id="content-left-nav"
								lg={4}
								flexDirection="column"
								className={clsx(
									'transition-all duration-300 ease-in-out overflow-hidden',
									isLeftNavCollapsed
										? 'lg:!w-0 lg:!min-w-0 lg:!max-w-0 lg:!flex-[0_0_0%] lg:!h-0 lg:!py-0 lg:!my-0 lg:opacity-0'
										: 'lg:!w-auto lg:!min-w-0 lg:!flex-[0_0_33.333333%] lg:!h-auto lg:opacity-100',
								)}
							>
								<div
									className={clsx(
										'transition-all duration-300 ease-in-out',
										isLeftNavCollapsed
											? 'transform lg:-translate-x-4'
											: 'transform lg:translate-x-0',
									)}
								>
									<>
										{/* Slot */}
										{renderSlotContentLeft &&
											renderSlotContentLeft()}
										{/* Desktop: Current focus area and its related */}
										{((!isFetchedFocusArea || isLoading) &&
											isFocusAreaNavigateInView) ||
										!currentFocusAreaOrOptionList ? (
											<div className="mb-8 space-y-4 hidden lg:block">
												<FocusAreaSkeleton />
												{isCurrentStage4Or5Or6 && (
													<FocusAreaSkeleton />
												)}
											</div>
										) : (
											<div className="mb-8 space-y-4 hidden lg:block">
												<FocusAreaPaper
													currentStage={stageId}
													focusAreaOrOptionlistOrFocusareaoption={
														currentFocusAreaOrOptionList
													}
													outcomes={
														currentSyllabusCourse
															? getSyllabusCourseFocusAreaOutcomes(
																	currentFocusAreas,
																	currentFocusAreaCodename,
																	linkedItems,
																	currentFocusAreaOutcomes,
															  )
															: currentFocusAreaOutcomes
													}
													linkedItems={linkedItems}
													mappings={mappings}
													isLifeSkill={isLifeSkillFocusAreaOrOptionListOrOutcome(
														currentFocusAreaOrOptionList,
													)}
													slotBeforeOutcomes={
														isShowOutcomesNotification && (
															<Alert
																as="info"
																className="mt-1 mb-3 text-nsw-grey-01"
															>
																{renderOutcomeNotificationList(
																	filteredOutcomesNotificationList,
																	mappings,
																	linkedItems,
																	currentStageBasedOnViewLifeskillsToggle,
																	currentYearBasedOnViewLifeskillsToggle,
																)}
															</Alert>
														)
													}
													selected
												/>
												{isCurrentStage4Or5Or6 && (
													<FocusAreaRelatedPaper
														syllabus={syllabus}
														mainFocusArea={
															selectedFocusAreaOption ||
															(currentFocusAreaOrOptionList as Focusarea)
														}
														currentStage={stageId}
														currentYear={
															currentSyllabusCourse
																? currentSyllabusCourseYearId
																: yearId
														}
														onTitleClick={
															!isWithPreloadFocusAreaAndDeepLinkNavigation
																? onRelatedFocusAreaTitleClick
																: undefined
														}
														linkedItems={
															linkedItems
														}
														qsOnUrl={qsOnSideNavs}
														selectedFocusAreaOptionCodename={
															selectedFocusAreaOptionCodename
														}
														currentCourseCodename={
															currentSyllabusCourse
																? currentSyllabusCourse
																		.system
																		.codename
																: null
														}
														nonClickableTitle={
															disableLifeSkillCheckbox
														}
													/>
												)}
											</div>
										)}
										<ContentSideNav
											focusAreas={
												currentFocusAreasForSideNav
											}
											currentFocusAreaCodename={
												currentFocusAreaCodename
											}
											currentStage={stageId}
											currentYear={
												currentSyllabusCourse
													? currentSyllabusCourseYearId
													: yearId
											}
											currentSyllabusCodename={
												syllabus.system.codename
											}
											onNavItemClick={
												isWithPreloadFocusAreaAndDeepLinkNavigation
													? undefined
													: handleOnSideNavItemClick
											}
											qsOnUrl={qsOnSideNavs}
											preview={preview}
											linkedItems={linkedItems}
										/>
									</>
								</div>
							</GridCol>

							{/* Right */}
							<GridCol
								lg={isLeftNavCollapsed ? 12 : 8}
								className={clsx(
									'transition-all duration-300 ease-in-out',
									isLeftNavCollapsed
										? 'lg:!flex-[0_0_100%] lg:!max-w-full'
										: 'lg:!flex-[0_0_66.666667%] lg:!max-w-[66.666667%]',
								)}
							>
								{((!isFetchedFocusArea || isLoading) &&
									isFocusAreaNavigateInView) ||
								!currentFocusAreaOrOptionList ? (
									<OutcomeDetailCardSkeleton />
								) : (
									<>
										<div
											id={`scrollhereonfocusareaselected-${stageId}${
												yearId ? `-${yearId}` : ''
											}`}
											data-stage={stageId}
											data-year={yearId}
											ref={
												refScrollHereOnFocusAreaSelected
											}
											css={{
												'.is-preview &': {
													scrollMarginTop: 26,
												},
											}}
										/>

										<div className={clsx('space-y-8')}>
											{
												// on desktop we display the content on the right
												selectedFocusAreaCodename && (
													<OutcomeDetailCard
														className={clsx(
															'border-t-8 ',
															isCurrentFocusAreaLifeSkills &&
																'border-t-nsw-brand-light',
															!isCurrentFocusAreaLifeSkills &&
																'border-t-nsw-brand-dark',
														)}
														data-kontent-item-id={
															currentFocusAreaOrOptionList
																?.system.id
														}
														stage={stageId}
														year={
															yearId ===
															'life_skills'
																? undefined
																: yearId
														}
														groups={
															currentFocusAreaContentGroups
														}
														accessPointContent={
															currentFocusAreaAccessPointContent
														}
														accessPoints={
															currentContentOrganiserAccessPointGroups
														}
														showAccessPoints={
															showAccessPoints
														}
														showTags={tagIds}
														showExamples={
															shouldEnableExamplesCheckbox &&
															showExamples
														}
														linkedItems={
															linkedItems
														}
														mappings={mappings}
														slotBeforeContentGroups={
															<>
																{!isRichtextElementEmpty(
																	currentFocusAreaOrOptionList
																		?.elements
																		.content,
																) && (
																	<RichText
																		richTextElement={
																			currentFocusAreaOrOptionList
																				?.elements
																				.content
																		}
																		linkedItems={
																			linkedItems
																		}
																		mappings={
																			mappings
																		}
																		css={getScrollMarginBasedOnODT(
																			cssVarFaoOdtHeight,
																			cssVarStickyDropdownHeight,
																		)}
																		copyUrlPrefix={
																			focusAreaOrFocusAreaOptionPath
																		}
																	/>
																)}

																{!!currentStagedContents.length && (
																	<div className="space-y-3">
																		{currentStagedContents.map(
																			(
																				stagedContent,
																			) => (
																				<Contentrichtext
																					key={
																						stagedContent
																							.system
																							.id
																					}
																					linkedItem={
																						stagedContent
																					}
																					css={getScrollMarginBasedOnODT(
																						cssVarFaoOdtHeight,
																						cssVarStickyDropdownHeight,
																					)}
																					linkedItems={
																						linkedItems
																					}
																					mappings={
																						mappings
																					}
																				/>
																			),
																		)}
																	</div>
																)}

																{/* If it's an optionlist OR focusarea option */}
																{!isCurrentFocusAreaFocusArea && (
																	<>
																		<WrapperWithInView
																			onInViewChange={
																				handleFaoDropdownInViewChange
																			}
																			css={{
																				'&.WrapperWithInView-trigger':
																					{
																						// cssVarFaoOdtHeight is the bare
																						// custom-property name, so it must be
																						// wrapped in var(). Without it the calc
																						// was invalid and the transform silently
																						// dropped, leaving this trigger un-offset
																						// so it fired a full title-height late.
																						transform: `translateY(calc(var(${cssVarFaoOdtHeight}) * -1))`,
																					},
																			}}
																		>
																			{(
																				inView,
																			) => {
																				return (
																					<div
																						className={clsx(
																							'sticky z-[2] border-t-2 border-b-2 border-white bg-white -mx-8 px-8 pb-2 !-mb-2 transition',
																							!inView &&
																								'border-b-black',
																							'Content__faoDropdown',
																						)}
																						css={{
																							top: preview
																								? `calc(var(${cssVarFaoOdtHeight}) + 26px - 2px)`
																								: `calc(var(${cssVarFaoOdtHeight}) - 2px)`,
																						}}
																					>
																						<FormGroupSelect
																							key={
																								selectedFocusAreaOptionCodename
																							}
																							selected={
																								selectedFocusAreaOptionCodename
																							}
																							className="border-nsw-blue-01 border-2 font-bold text-nsw-blue-01 truncate"
																							label="Select focus area option"
																							placeholder=""
																							options={currentFocusAreaOptions.map<FormOption>(
																								(
																									option,
																								) => {
																									return {
																										text: option
																											.elements
																											.title
																											.value,
																										value: option
																											.system
																											.codename,
																									}
																								},
																							)}
																							onChange={
																								handleFocusareaOptionsDropdownChange
																							}
																							hideLabel
																							autoComplete="off"
																						/>
																					</div>
																				)
																			}}
																		</WrapperWithInView>

																		{selectedFocusAreaOption && (
																			<CommonCopyUrlWrapper
																				url={
																					focusAreaOrFocusAreaOptionPath
																				}
																				className="nsw-h4 my-8"
																			>
																				{
																					selectedFocusAreaOption
																						.elements
																						.title
																						.value
																				}
																			</CommonCopyUrlWrapper>
																		)}

																		{!isRichtextElementEmpty(
																			selectedFocusAreaOption
																				?.elements
																				.content,
																		) && (
																			<RichText
																				linkedItems={
																					linkedItems
																				}
																				mappings={
																					mappings
																				}
																				richTextElement={
																					selectedFocusAreaOption
																						?.elements
																						.content
																				}
																				css={getScrollMarginBasedOnODT(
																					cssVarFaoOdtHeight,
																					cssVarStickyDropdownHeight,
																				)}
																			/>
																		)}

																		{!!selectedFocusAreaOptionStagedContents.length && (
																			<div className="space-y-3">
																				{selectedFocusAreaOptionStagedContents.map(
																					(
																						stagedContent,
																					) => (
																						<Contentrichtext
																							key={
																								stagedContent
																									.system
																									.id
																							}
																							linkedItem={
																								stagedContent
																							}
																							css={getScrollMarginBasedOnODT(
																								cssVarFaoOdtHeight,
																								cssVarStickyDropdownHeight,
																							)}
																							linkedItems={
																								linkedItems
																							}
																							mappings={
																								mappings
																							}
																						/>
																					),
																				)}
																			</div>
																		)}
																	</>
																)}
															</>
														}
														slotBeforeTitle={
															isCurrentFocusAreaLifeSkills &&
															!isRichtextElementEmpty(
																lifeSkillsInfoForFocusArea,
															) && (
																<div>
																	<div className="nsw-h4 mb-8">
																		{getLifeSkillsForStageLabel(
																			currentFocusAreaOrOptionList,
																		)}
																	</div>
																	<RichText
																		richTextElement={
																			lifeSkillsInfoForFocusArea
																		}
																		linkedItems={
																			linkedItems
																		}
																		mappings={
																			mappings
																		}
																		copyUrlPrefix={
																			focusAreaOrFocusAreaOptionPath
																		}
																		css={getScrollMarginBasedOnODT(
																			cssVarFaoOdtHeight,
																			cssVarStickyDropdownHeight,
																		)}
																	/>
																</div>
															)
														}
														focusAreaOrFocusAreaOptionPath={
															focusAreaOrFocusAreaOptionPath
														}
														slotAfterContentGroups={
															<>
																{(isFocusarea(
																	currentFocusAreaOptionOrFocusAreaOrOptionlist,
																) ||
																	isFocusareaoption(
																		currentFocusAreaOptionOrFocusAreaOrOptionlist,
																	)) &&
																	(currentFocusAreaOptionOrFocusAreaOrOptionlist
																		.elements
																		.prescriptions
																		?.value
																		?.length ??
																		0) >
																		0 && (
																		<PrescriptionSection
																			currentFocusArea={
																				currentFocusAreaOptionOrFocusAreaOrOptionlist
																			}
																			syllabus={
																				syllabus
																			}
																			linkedItems={
																				linkedItems
																			}
																			mappings={
																				mappings
																			}
																		/>
																	)}
																{previousAndNextFocusAreaOptionAsSideNavItem && (
																	<div className="flex justify-between gap-6">
																		{previousAndNextFocusAreaOptionAsSideNavItem.prev ? (
																			<PrevNextButton
																				className="[&_.Link]:text-nsw-brand-dark [&_.Link]:font-bold [&_.Link]:underline [&_.Link:hover]:bg-nsw-text-hover [&_.Link:focus]:outline-3 [&_.Link:focus]:outline [&_.Link:focus]:outline-[var(--nsw-focus)]"
																				type="Previous"
																				contentItem={
																					previousAndNextFocusAreaOptionAsSideNavItem.prev
																				}
																			>
																				<span>
																					Previous
																				</span>
																			</PrevNextButton>
																		) : (
																			<span />
																		)}
																		{previousAndNextFocusAreaOptionAsSideNavItem.next ? (
																			<PrevNextButton
																				className="[&_.Link]:text-nsw-brand-dark [&_.Link]:font-bold [&_.Link]:underline [&_.Link:hover]:bg-nsw-text-hover [&_.Link:focus]:outline-3 [&_.Link:focus]:outline [&_.Link:focus]:outline-[var(--nsw-focus)]"
																				type="Next"
																				contentItem={
																					previousAndNextFocusAreaOptionAsSideNavItem.next
																				}
																			>
																				<span>
																					Next
																				</span>
																			</PrevNextButton>
																		) : (
																			<span />
																		)}
																	</div>
																)}
															</>
														}
														allOverarchingLinks={
															allOverarchingLinks
														}
														cssVariablesForODTTotalHeight={[
															cssVarFaoOdtHeight,
															cssVarStickyDropdownHeight,
														]}
														selectedLanguages={
															selectedExampleLanguagesArray
														}
														selectedPathways={
															selectedPathwaysBasedOnSyllabus
														}
													>
														<OutcomeDetailCardTitle
															variant={
																isCurrentFocusAreaLifeSkills
																	? 'brand-light'
																	: 'brand-dark'
															}
															showLeftNavToggle
															// the focus-area option dropdown sticks flush under this
															// title, and the title paints above it (see !z-[20] there),
															// so the sticky bottom border would draw a line across the
															// dropdown. Once the dropdown is stuck it carries its own
															// bottom border, so drop this one only from that point on.
															// `!transition-none` cancels the title's `transition-all
															// duration-150`, which would otherwise fade the border out
															// over 150ms instead of clearing it the moment they meet.
															whenStickyClassNames={
																!isCurrentFocusAreaFocusArea &&
																isFaoDropdownStuck
																	? '!border-b-transparent !transition-none'
																	: ''
															}
															leftNavRegionId="content-left-nav"
															cssVariableForHeight={
																cssVarFaoOdtHeight
															}
															useInViewOptions={{
																rootMargin:
																	preview
																		? '-58px 0px 0px 0px'
																		: '-32px 0px 0px 0px',
															}}
															isHideTopBorderInitially={
																isCurrentFocusAreaLifeSkills &&
																!isRichtextElementEmpty(
																	lifeSkillsInfoForFocusArea,
																)
															}
														>
															<CommonCopyUrlWrapper
																url={
																	focusAreaOrFocusAreaOptionPath
																}
															>
																{
																	currentFocusAreaOrOptionList
																		?.elements
																		.title
																		.value
																}
															</CommonCopyUrlWrapper>
														</OutcomeDetailCardTitle>
													</OutcomeDetailCard>
												)
											}
											<div>
												<div
													id={`scrollhereonteachingsupport-${stageId}${
														yearId
															? `-${yearId}`
															: ''
													}`}
													data-stage={stageId}
													data-year={yearId}
													ref={
														refScrollHereOnTeachingSupportEnabled
													}
													css={{
														position: 'relative',
														'.is-preview &': {
															top: -26,
														},
														height:
															isClient &&
															showTeachingSupport
																? 12
																: undefined,
														marginTop:
															isClient &&
															showTeachingSupport
																? -12
																: undefined,
													}}
												></div>
												{showTeachingSupport &&
													isClient &&
													currentFocusAreaCodename && (
														<>
															{currentStageOrYearTeachingAdvices.length
																? currentStageOrYearTeachingAdvices.map(
																		(
																			teachingAdvice: Teachingadvice,
																		) => {
																			return (
																				<TeachingSupportCard
																					key={
																						teachingAdvice
																							.system
																							.id
																					}
																					data-kontent-item-id={
																						teachingAdvice
																							.system
																							.id
																					}
																					mappings={
																						mappings
																					}
																					linkedItems={
																						linkedItems
																					}
																					content={
																						teachingAdvice
																							.elements
																							.content
																					}
																					currentSyllabus={
																						syllabus
																					}
																					currentStage={
																						stageId
																					}
																					currentYear={
																						yearId
																					}
																					isLifeSkillMode={
																						selectedFilterViewLifeSkill
																					}
																					title={
																						<OutcomeDetailCardTitle
																							className="!bg-nsw-off-white !border-t-0"
																							whenStickyClassNames="!px-6 md:!px-8 !py-4"
																							indicatorClassName={css(
																								{
																									top: 1,
																									'.is-preview &':
																										{
																											position:
																												'relative',
																											top: -16,
																										},
																								},
																							)}
																							ref={
																								refTeachingSupportTopForTitle
																							}
																							css={{
																								'&&': {
																									margin: '-1.5rem',
																									padding:
																										'1.5rem',
																									paddingBottom:
																										'.5rem',
																									marginBottom:
																										'1rem',

																									'@media (min-width: 768px)':
																										{
																											margin: '-2rem',
																											padding:
																												'2rem',
																											paddingBottom:
																												'1rem',
																											marginBottom:
																												'-1rem',
																										},
																								},
																							}}
																							cssVariableForHeight="--ta-odt-height"
																							useInViewOptions={{
																								rootMargin:
																									preview
																										? '-58px 0px 0px 0px'
																										: '-32px 0px 0px 0px',
																							}}
																							isHideTopBorderInitially={
																								isCurrentFocusAreaLifeSkills &&
																								!isRichtextElementEmpty(
																									lifeSkillsInfoForFocusArea,
																								)
																							}
																						>
																							<CommonCopyUrlWrapper
																								url={
																									currentTeachingAdvicePath
																								}
																							>
																								{'Teaching advice for ' +
																									currentFocusAreaOptionOrFocusAreaOrOptionlist
																										?.elements
																										.title
																										.value}
																							</CommonCopyUrlWrapper>
																						</OutcomeDetailCardTitle>
																					}
																					css={{
																						'& [id]':
																							{
																								scrollMarginTop:
																									'calc(var(--ta-odt-height, 0px) + 24px)',
																							},
																					}}
																				>
																					{!!teachingAdviceFiles?.length && (
																						<DownloadList
																							key={`teachingAdviceFiles-${currentFocusAreaCodename}`}
																							className="mt-8"
																							files={
																								teachingAdviceFiles
																							}
																							hideCheckbox
																							hiddenFields={
																								downloadListHiddenFields
																							}
																							pageType={
																								pageType
																							}
																						/>
																					)}
																				</TeachingSupportCard>
																			)
																		},
																  )
																: renderNoTeachingAdvice()}
														</>
													)}
											</div>

											{!!nonTeachingAdviceFiles?.length && (
												<Paper
													variant="outlined"
													className="p-4 md:p-8"
												>
													<div className="nsw-h3">
														Related files
													</div>

													<DownloadList
														key={`nonTeachingAdviceFiles-${currentFocusAreaCodename}`}
														className="mt-4"
														files={
															nonTeachingAdviceFiles
														}
														hideCheckbox
														hiddenFields={
															downloadListHiddenFields
														}
														pageType={pageType}
													/>
												</Paper>
											)}
											{/* Full-width (collapsed) only: prev/next focus area at end of panel */}
											{isLeftNavCollapsed &&
												previousAndNextFocusAreaAsSideNavItem && (
													<FocusAreaPrevNextNav
														prev={
															previousAndNextFocusAreaAsSideNavItem.prev
														}
														next={
															previousAndNextFocusAreaAsSideNavItem.next
														}
													/>
												)}
										</div>
									</>
								)}
							</GridCol>
						</Grid>
					</div>
				</div>
				<CustomPopover
					title="Select tags to begin"
					popoverStatus={tagsHoverPopover}
					popoverAnchor={tagsPopoverAnchor}
					onConfirm={handleTagPopoverConfirm}
					onCancel={() => setTagsHoverPopover(false)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						horizontal: 'right',
						vertical: 'top',
					}}
				>
					<div>
						<TagPicker
							initialSearchText={tagSearchText}
							selected={tempTagIds ?? []}
							onChange={handleTagsChange}
							onSearch={handleTagSearch}
						/>
					</div>
				</CustomPopover>
				<LanguagePicker
					syllabuses={syllabusesForExampleLanguagePicker}
					languages={languageOptions}
					initialSelectedLanguages={selectedExampleLanguages}
					title={languagePickerTitle}
					modalStatus={isShowLanguageOptions}
					handleCancel={() => {
						toggleLanguageOptions()
					}}
					handleConfirm={async (languages) => {
						dispatch({
							type: 'SET_SELECTEDEXAMPLELANGUAGES',
							payload: languages,
						})
						toggleLanguageOptions()
					}}
					resetSelectedLanguagesOnClose={false}
					slotBeforeSearch={slotBeforeSearchContent}
					isValidationNotRequired
				></LanguagePicker>
				<CustomPopover
					title="Select Pathway"
					popoverStatus={pathwaysHoverPopover}
					popoverAnchor={pathwaysPopoverAnchor}
					onConfirm={handlePathwaysPopoverConfirm}
					onCancel={() => {
						setTempPathways(selectedPathways)
						setPathwaysHoverPopover(false)
					}}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						horizontal: 'right',
						vertical: 'top',
					}}
				>
					<div>
						{syllabus.elements.pathways.value.map(
							(pathway, _index, _allPathways) => {
								return (
									<div key={pathway.codename}>
										<FormControlLabel
											htmlFor={pathway.codename}
											className="mx-0"
											control={
												<Checkbox
													id={pathway.codename}
												/>
											}
											label={pathway.name}
											onChange={(_e, checked) => {
												if (checked) {
													setTempPathways([
														...tempPathways,
														pathway,
													])
												} else {
													const unselecteds =
														tempPathways.filter(
															(p) =>
																p.codename !==
																pathway.codename,
														)
													setTempPathways(unselecteds)
												}
											}}
											checked={tempPathways
												.map(byTaxoCodename)
												.includes(pathway.codename)}
										/>
									</div>
								)
							},
						)}
					</div>
				</CustomPopover>
			</div>
		)
	},
)

const Content = forwardRef<HTMLDivElement, ContentOrganizerProps>(
	(props, ref) => {
		return (
			<LeftNavProvider>
				<ContentInner {...props} ref={ref} />
			</LeftNavProvider>
		)
	},
)

export default Content
